/**
 * Geo Manager — Main Orchestrator
 * ================================
 *
 * Imports modular components and wires them together.
 * Sets window.GeoManager for template compatibility.
 *
 * Architecture:
 *   modules/state.js      — Shared state, config, eventManager
 *   modules/api.js        — API client (all HTTP calls)
 *   modules/utils.js      — Notifications, escapeHtml, modals
 *   modules/events.js     — Event bus for decoupled communication
 *   modules/persistence.js— LocalStorage backup/restore
 *   modules/map-ops.js    — Leaflet map, markers, zones
 */

import { state, config, eventManager } from './modules/state.js';
import { api } from './modules/api.js';
import { escapeHtml, formatNumber, showNotification, createModal, closeModal } from './modules/utils.js';
import { emit, on } from './modules/events.js';
import { saveToLocalStorage, loadFromLocalStorage } from './modules/persistence.js';
import {
    initMap, updateMapMarkers, addMarkerToMap, addZoneToMap,
    fitMapToPoints, centerOnPoint, highlightMarker, filterPointsByFolder
} from './modules/map-ops.js';

// ============================================================================
// Lazy-load export libraries (html2canvas, jsPDF, XLSX)
// ============================================================================

function _loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(s);
    });
}

async function ensureExportLibs() {
    const libs = [];
    if (typeof html2canvas !== 'function')
        libs.push(_loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'));
    if (typeof jspdf === 'undefined')
        libs.push(_loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'));
    if (libs.length > 0) await Promise.all(libs);
}

async function ensureXLSX() {
    if (typeof XLSX === 'undefined')
        await _loadScript('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js');
}

// ============================================================================
// UI - Dossiers (Arborescence)
// ============================================================================

function renderFolderTree() {
    const container = document.getElementById('geo-folder-tree');
    if (!container) return;

    const rootFolders = state.folders.filter(f => !f.parent_id);

    if (state.folders.length === 0) {
        container.innerHTML = `
            <div class="text-center text-gray-500 py-4">
                <i class="fas fa-folder-open text-2xl mb-2"></i>
                <p class="text-sm">Aucun dossier</p>
                <button onclick="GeoManager.showCreateFolderModal()" class="btn-link text-sm mt-2">
                    + Créer un dossier
                </button>
            </div>
        `;
        return;
    }

    container.innerHTML = buildFolderTreeHtml(rootFolders, 0);
    attachFolderEvents();
}

function buildFolderTreeHtml(folders, level) {
    return folders.map(folder => {
        const children = state.folders.filter(f => f.parent_id === folder.id);
        const hasChildren = children.length > 0;
        const isSelected = state.selectedFolder === folder.id;
        const pointCount = state.points.filter(p => p.folder_id === folder.id).length;
        const iconClass = config.folderIcons[folder.icon] || 'fa-folder';
        const isVisible = folder.is_visible !== false && folder.is_visible !== 0;

        return `
            <div class="folder-item ${isSelected ? 'selected' : ''} ${!isVisible ? 'folder-hidden' : ''}"
                 data-folder-id="${folder.id}"
                 draggable="true"
                 style="padding-left: ${level * 16 + 8}px">
                <div class="folder-row" onclick="GeoManager.selectFolder('${folder.id}')">
                    ${hasChildren ? `
                        <i class="fas fa-chevron-right folder-toggle" onclick="event.stopPropagation(); GeoManager.toggleFolderExpand('${folder.id}')"></i>
                    ` : '<span class="folder-toggle-spacer"></span>'}
                    <i class="fas ${iconClass}" style="color: ${folder.color}"></i>
                    <span class="folder-name">${escapeHtml(folder.name)}</span>
                    <span class="folder-count">${pointCount}</span>
                    <button class="folder-visibility ${isVisible ? '' : 'hidden'}"
                            onclick="event.stopPropagation(); GeoManager.toggleFolderVisibility('${folder.id}')"
                            title="${isVisible ? 'Masquer les points' : 'Afficher les points'}">
                        <i class="fas ${isVisible ? 'fa-eye' : 'fa-eye-slash'}"></i>
                    </button>
                    <div class="folder-actions">
                        <button onclick="event.stopPropagation(); GeoManager.showEditFolderModal('${folder.id}')" title="Modifier">
                            <i class="fas fa-pen"></i>
                        </button>
                        <button onclick="event.stopPropagation(); GeoManager.deleteFolder('${folder.id}')" title="Supprimer">
                            <i class="fas fa-trash"></i>
                        </button>
                    </div>
                </div>
                ${hasChildren ? `
                    <div class="folder-children" id="folder-children-${folder.id}">
                        ${buildFolderTreeHtml(children, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    }).join('');
}

// Drag & Drop state
let draggedItem = null;
let draggedType = null;  // 'folder' or 'point'

function handleFolderDragStart(e, folderId) {
    draggedItem = folderId;
    draggedType = 'folder';
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', folderId);
    e.target.closest('.folder-item').classList.add('dragging');
}

function handleFolderDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const target = e.target.closest('.folder-item');
    if (target && !target.classList.contains('drag-over')) {
        document.querySelectorAll('.folder-item.drag-over').forEach(el => el.classList.remove('drag-over'));
        target.classList.add('drag-over');
    }
}

function handleFolderDragLeave(e) {
    const target = e.target.closest('.folder-item');
    if (target) {
        target.classList.remove('drag-over');
    }
}

async function handleFolderDrop(e, targetFolderId) {
    e.preventDefault();
    document.querySelectorAll('.folder-item.dragging, .folder-item.drag-over').forEach(el => {
        el.classList.remove('dragging', 'drag-over');
    });

    if (!draggedItem || draggedType !== 'folder' || draggedItem === targetFolderId) {
        draggedItem = null;
        draggedType = null;
        return;
    }

    // Vérifier qu'on ne déplace pas un dossier dans un de ses sous-dossiers
    if (isChildFolder(draggedItem, targetFolderId)) {
        showNotification('Impossible de déplacer un dossier dans un de ses sous-dossiers', 'error');
        draggedItem = null;
        draggedType = null;
        return;
    }

    try {
        await api.updateFolder(draggedItem, { parent_id: targetFolderId });
        await loadData();
        showNotification('Dossier déplacé', 'success');
    } catch (error) {
        showNotification(error.message, 'error');
    }

    draggedItem = null;
    draggedType = null;
}

function isChildFolder(parentId, childId) {
    // Vérifie si childId est un descendant de parentId
    let current = childId;
    while (current) {
        const folder = state.folders.find(f => f.id === current);
        if (!folder) break;
        if (folder.parent_id === parentId) return true;
        current = folder.parent_id;
    }
    return false;
}

function handlePointDragStart(e, pointId) {
    draggedItem = pointId;
    draggedType = 'point';
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', pointId);
    e.target.closest('.point-item').classList.add('dragging');
}

async function handlePointDropOnFolder(e, folderId) {
    e.preventDefault();
    document.querySelectorAll('.point-item.dragging, .folder-item.drag-over').forEach(el => {
        el.classList.remove('dragging', 'drag-over');
    });

    if (!draggedItem || draggedType !== 'point') {
        draggedItem = null;
        draggedType = null;
        return;
    }

    try {
        await api.updatePoint(draggedItem, { folder_id: folderId });
        await loadData();
        showNotification('Point déplacé dans le dossier', 'success');
    } catch (error) {
        showNotification(error.message, 'error');
    }

    draggedItem = null;
    draggedType = null;
}

function attachFolderEvents() {
    // Events attachés directement dans le HTML généré
}

function selectFolder(folderId) {
    state.selectedFolder = folderId;
    renderFolderTree();
    filterPointsByFolder(folderId);
    updatePointsList();
}

function toggleFolderExpand(folderId) {
    const children = document.getElementById(`folder-children-${folderId}`);
    if (children) {
        children.classList.toggle('collapsed');
        const toggle = document.querySelector(`[data-folder-id="${folderId}"] .folder-toggle`);
        if (toggle) {
            toggle.classList.toggle('fa-chevron-right');
            toggle.classList.toggle('fa-chevron-down');
        }
    }
}

async function toggleFolderVisibility(folderId) {
    const folder = state.folders.find(f => f.id === folderId);
    if (!folder) return;

    const newVisibility = folder.is_visible === false || folder.is_visible === 0 ? true : false;

    try {
        await api.updateFolder(folderId, { is_visible: newVisibility });
        folder.is_visible = newVisibility;

        // Re-render
        renderFolderTree();
        updateMapMarkers(selectPoint);

        showNotification(
            newVisibility ? 'Points du dossier affichés' : 'Points du dossier masqués',
            'info'
        );
    } catch (error) {
        showNotification(error.message, 'error');
    }
}

// ============================================================================
// UI - Points
// ============================================================================


// ============================================================================
// UI - Points
// ============================================================================

function renderPoints() {
    updatePointsList();
    updateMapMarkers(selectPoint);
}

function updatePointsList() {
    const container = document.getElementById('geo-points-list');
    if (!container) return;

    // Bug #18 Fix: Si un dossier est sélectionné, montrer ses points
    // Sinon, montrer uniquement les points sans dossier (folder_id null/undefined)
    const filteredPoints = state.selectedFolder
        ? state.points.filter(p => p.folder_id === state.selectedFolder)
        : state.points.filter(p => !p.folder_id);

    if (filteredPoints.length === 0) {
        container.innerHTML = `
            <div class="text-center text-gray-500 py-4">
                <i class="fas fa-map-marker-alt text-2xl mb-2"></i>
                <p class="text-sm">Aucun point</p>
            </div>
        `;
        return;
    }

    container.innerHTML = filteredPoints.map(point => {
        const isSelected = state.selectedPoint === point.id;
        const iconData = config.icons.preset[point.icon_value] || config.icons.preset['default'];

        return `
            <div class="point-item ${isSelected ? 'selected' : ''}"
                 data-point-id="${point.id}"
                 draggable="true"
                 ondragstart="GeoManager.handlePointDragStart(event, '${point.id}')"
                 onclick="GeoManager.selectPoint('${point.id}')">
                <div class="point-icon" style="background-color: ${point.color || config.defaultColor}">
                    <i class="fas ${iconData.icon}"></i>
                </div>
                <div class="point-info">
                    <div class="point-name">${escapeHtml(point.name)}</div>
                    <div class="point-coords">${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}</div>
                </div>
                <div class="point-actions">
                    <button onclick="event.stopPropagation(); GeoManager.centerOnPoint('${point.id}')" title="Centrer">
                        <i class="fas fa-crosshairs"></i>
                    </button>
                    <button onclick="event.stopPropagation(); GeoManager.showEditPointModal('${point.id}')" title="Modifier">
                        <i class="fas fa-pen"></i>
                    </button>
                    <button onclick="event.stopPropagation(); GeoManager.deletePoint('${point.id}')" title="Supprimer">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>
            </div>
        `;
    }).join('');
}

// filterPointsByFolder imported from map-ops.js

function selectPoint(pointId) {
    state.selectedPoint = pointId;
    updatePointsList();

    const point = state.points.find(p => p.id === pointId);
    if (point) {
        showPointDetails(point);
        highlightMarker(pointId);
    }
}

function showPointDetails(point) {
    const container = document.getElementById('geo-point-details');
    if (!container) return;

    const iconData = config.icons.preset[point.icon_value] || config.icons.preset['default'];
    const folder = state.folders.find(f => f.id === point.folder_id);

    container.innerHTML = `
        <div class="point-details-header">
            <div class="point-details-icon" style="background-color: ${point.color || config.defaultColor}">
                <i class="fas ${iconData.icon}"></i>
            </div>
            <div>
                <h3>${escapeHtml(point.name)}</h3>
                ${folder ? `<span class="text-gray-500 text-sm"><i class="fas fa-folder"></i> ${escapeHtml(folder.name)}</span>` : ''}
            </div>
        </div>

        <div class="point-details-section">
            <h4>Coordonnées</h4>
            <p>${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}</p>
        </div>

        ${point.description ? `
            <div class="point-details-section">
                <h4>Description</h4>
                <p>${escapeHtml(point.description)}</p>
            </div>
        ` : ''}

        ${point.zone_type !== 'none' ? `
            <div class="point-details-section">
                <h4>Zone de chalandise</h4>
                <p>${point.zone_value} ${point.zone_type === 'minutes' ? 'minutes' : 'km'}</p>
            </div>
        ` : ''}

        <div class="point-details-actions">
            <button class="btn btn-primary btn-sm" onclick="GeoManager.showEditPointModal('${point.id}')">
                <i class="fas fa-pen"></i> Modifier
            </button>
            <button class="btn btn-secondary btn-sm" onclick="GeoManager.centerOnPoint('${point.id}')">
                <i class="fas fa-crosshairs"></i> Centrer
            </button>
        </div>

        <!-- Analyses -->
        <div class="point-details-section mt-4">
            <h4>Analyses</h4>
            <div class="analysis-buttons">
                <button class="btn btn-sm" onclick="GeoManager.loadPointDemographics('${point.id}')">
                    <i class="fas fa-users"></i> Démographie
                </button>
                <button class="btn btn-sm" onclick="GeoManager.loadPointCompetitors('${point.id}')">
                    <i class="fas fa-store-slash"></i> Concurrents
                </button>
            </div>
        </div>
        <div id="point-analysis-results"></div>
    `;
}

async function loadPointDemographics(pointId) {
    const resultsContainer = document.getElementById('point-analysis-results');
    if (!resultsContainer) return;

    resultsContainer.innerHTML = '<div class="loading-text"><i class="fas fa-spinner fa-spin"></i> Chargement...</div>';

    try {
        const result = await api.getPointDemographics(pointId);
        if (result.success && result.demographics) {
            const d = result.demographics;
            resultsContainer.innerHTML = `
                <div class="analysis-result">
                    <h5><i class="fas fa-users"></i> Données démographiques (${d.radius_km}km)</h5>
                    <div class="analysis-stats">
                        <div class="analysis-stat-item">
                            <div class="label">Population</div>
                            <div class="value">${d.population.toLocaleString()}</div>
                        </div>
                        <div class="analysis-stat-item">
                            <div class="label">Ménages</div>
                            <div class="value">${d.menages.toLocaleString()}</div>
                        </div>
                        <div class="analysis-stat-item">
                            <div class="label">Revenu médian</div>
                            <div class="value">${d.revenu_median.toLocaleString()}<span class="unit">€</span></div>
                        </div>
                        <div class="analysis-stat-item">
                            <div class="label">Communes</div>
                            <div class="value">${d.communes_count}</div>
                        </div>
                    </div>
                </div>
            `;
        } else {
            resultsContainer.innerHTML = `<p class="text-gray-500">${result.message || 'Données non disponibles'}</p>`;
        }
    } catch (error) {
        resultsContainer.innerHTML = `<p class="error-text">Erreur: ${error.message}</p>`;
    }
}

async function loadPointCompetitors(pointId) {
    const resultsContainer = document.getElementById('point-analysis-results');
    if (!resultsContainer) return;

    resultsContainer.innerHTML = '<div class="loading-text"><i class="fas fa-spinner fa-spin"></i> Recherche SIRENE...</div>';

    try {
        const result = await api.getPointCompetitors(pointId);
        if (result.success) {
            if (result.competitors && result.competitors.length > 0) {
                resultsContainer.innerHTML = `
                    <div class="analysis-result">
                        <h5><i class="fas fa-store-slash"></i> Concurrents (${result.count})</h5>
                        <div class="competitor-list">
                            ${result.competitors.slice(0, 5).map(c => `
                                <div class="competitor-item">
                                    <i class="fas fa-store"></i>
                                    <span class="name">${escapeHtml(c.nom || c.enseigne || 'N/A')}</span>
                                    <span class="distance">${c.distance ? c.distance.toFixed(1) + ' km' : ''}</span>
                                </div>
                            `).join('')}
                            ${result.count > 5 ? `<p class="no-data-message">+ ${result.count - 5} autres</p>` : ''}
                        </div>
                    </div>
                `;
            } else {
                resultsContainer.innerHTML = `<p class="text-gray-500">Aucun concurrent trouvé à proximité</p>`;
            }
        }
    } catch (error) {
        resultsContainer.innerHTML = `<p class="error-text">Erreur: ${error.message}</p>`;
    }
}

// ============================================================================
// Heatmap & Couverture
// ============================================================================

async function toggleHeatmap() {
    if (!state.map) return;

    const btn = document.getElementById('geo-heatmap-toggle');

    // Si heatmap active, la désactiver
    if (state.heatmapLayer) {
        state.map.removeLayer(state.heatmapLayer);
        state.heatmapLayer = null;
        if (btn) btn.classList.remove('active');
        showNotification('Heatmap désactivée', 'info');
        return;
    }

    // Afficher la modal de configuration heatmap
    showHeatmapModal();
}

function showHeatmapModal() {
    const modalHtml = `
        <div class="geo-modal active" id="heatmapModal">
            <div class="geo-modal-backdrop" onclick="GeoManager.closeModal('heatmapModal')"></div>
            <div class="geo-modal-content" style="max-width: 450px;">
                <div class="geo-modal-header">
                    <h3><i class="fas fa-fire"></i> Configuration Heatmap</h3>
                    <button class="geo-modal-close" onclick="GeoManager.closeModal('heatmapModal')">&times;</button>
                </div>
                <div class="geo-modal-body">
                    <div class="form-group">
                        <label>Type de heatmap</label>
                        <select id="heatmap-type" class="form-control">
                            <option value="density">Densité de points</option>
                            <option value="weighted">Points pondérés (métadonnées)</option>
                            <option value="coverage">Couverture des zones</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Rayon d'influence (px)</label>
                        <input type="range" id="heatmap-radius" class="form-control" min="10" max="50" value="25">
                        <small class="text-gray-500">25 px</small>
                    </div>
                    <div class="form-group">
                        <label>Intensité du flou</label>
                        <input type="range" id="heatmap-blur" class="form-control" min="5" max="30" value="15">
                        <small class="text-gray-500">15 px</small>
                    </div>
                    <div class="form-group">
                        <label>Palette de couleurs</label>
                        <select id="heatmap-gradient" class="form-control">
                            <option value="default">Par défaut (bleu → rouge)</option>
                            <option value="green">Vert (vert → jaune → rouge)</option>
                            <option value="purple">Violet (violet → rose → orange)</option>
                            <option value="grayscale">Niveaux de gris</option>
                        </select>
                    </div>
                    <div class="form-actions">
                        <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal('heatmapModal')">Annuler</button>
                        <button type="button" class="btn btn-primary" onclick="GeoManager.applyHeatmap()">
                            <i class="fas fa-fire"></i> Appliquer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // Event listeners pour les sliders
    document.getElementById('heatmap-radius').addEventListener('input', (e) => {
        e.target.nextElementSibling.textContent = e.target.value + ' px';
    });
    document.getElementById('heatmap-blur').addEventListener('input', (e) => {
        e.target.nextElementSibling.textContent = e.target.value + ' px';
    });
}

function applyHeatmap() {
    const heatmapType = document.getElementById('heatmap-type').value;
    const radius = parseInt(document.getElementById('heatmap-radius').value);
    const blur = parseInt(document.getElementById('heatmap-blur').value);
    const gradientName = document.getElementById('heatmap-gradient').value;

    closeModal('heatmapModal');

    // Vérifier si on a assez de points
    if (state.points.length < 2) {
        showNotification('Minimum 2 points requis pour la heatmap', 'warning');
        return;
    }

    try {
        // Supprimer l'ancienne heatmap si elle existe
        if (state.heatmapLayer) {
            state.map.removeLayer(state.heatmapLayer);
            state.heatmapLayer = null;
        }

        // Créer les données de heatmap selon le type
        let heatData;
        switch (heatmapType) {
            case 'weighted':
                // Utiliser les métadonnées (si disponibles) pour pondérer
                heatData = state.points.map(p => {
                    let weight = 1.0;
                    if (p.metadata) {
                        try {
                            const meta = typeof p.metadata === 'string' ? JSON.parse(p.metadata) : p.metadata;
                            // Audit Fix C2: parseFloat pour gérer les chaînes
                            const rawWeight = meta.weight || meta.population || meta.value;
                            weight = rawWeight ? parseFloat(rawWeight) : 1.0;
                            // Valider que c'est un nombre valide
                            if (isNaN(weight) || weight <= 0) weight = 1.0;
                        } catch (e) {}
                    }
                    return [p.lat, p.lng, Math.min(weight / 100, 1.0)];
                });
                break;
            case 'coverage':
                // Créer des points supplémentaires pour les zones
                heatData = [];
                state.points.forEach(p => {
                    if (p.zone_type === 'km' && p.zone_value) {
                        // Créer une grille de points pour représenter la zone
                        const radiusKm = p.zone_value;
                        const steps = 8;
                        for (let i = 0; i < steps; i++) {
                            const angle = (i / steps) * 2 * Math.PI;
                            for (let r = 0.2; r <= 1; r += 0.2) {
                                const dLat = (radiusKm * r / 111) * Math.cos(angle);
                                const dLng = (radiusKm * r / 111) * Math.sin(angle) / Math.cos(p.lat * Math.PI / 180);
                                heatData.push([p.lat + dLat, p.lng + dLng, 0.5]);
                            }
                        }
                    }
                    heatData.push([p.lat, p.lng, 1.0]);
                });
                break;
            default: // density
                heatData = state.points.map(p => [p.lat, p.lng, 1.0]);
        }

        // Définir le gradient
        const gradients = {
            default: { 0.0: 'blue', 0.25: 'cyan', 0.5: 'lime', 0.75: 'yellow', 1.0: 'red' },
            green: { 0.0: '#22c55e', 0.3: '#84cc16', 0.6: '#eab308', 0.85: '#f97316', 1.0: '#ef4444' },
            purple: { 0.0: '#8b5cf6', 0.3: '#d946ef', 0.6: '#f472b6', 0.85: '#fb923c', 1.0: '#fbbf24' },
            grayscale: { 0.0: '#f1f5f9', 0.25: '#94a3b8', 0.5: '#64748b', 0.75: '#334155', 1.0: '#0f172a' }
        };

        // Bug #15 Fix: Vérifier que L.heatLayer existe (plugin Leaflet.heat)
        if (typeof L === 'undefined' || typeof L.heatLayer !== 'function') {
            console.error('[Bug #15] Plugin Leaflet.heat non chargé');
            showNotification('Plugin heatmap non disponible. Rechargez la page.', 'error');
            return;
        }

        // Bug #15 Fix: Vérifier les données
        if (!heatData || heatData.length === 0) {
            console.error('[Bug #15] Aucune donnée pour la heatmap');
            showNotification('Aucune donnée disponible pour la heatmap', 'warning');
            return;
        }

        // Debug: heatmap creation logged only in development

        state.heatmapLayer = L.heatLayer(heatData, {
            radius: radius,
            blur: blur,
            maxZoom: 17,
            gradient: gradients[gradientName] || gradients.default
        }).addTo(state.map);

        const btn = document.getElementById('geo-heatmap-toggle');
        if (btn) btn.classList.add('active');

        const typeNames = { density: 'densité', weighted: 'pondérée', coverage: 'couverture' };
        showNotification(`Heatmap ${typeNames[heatmapType]} activée`, 'success');
    } catch (error) {
        console.error('Erreur heatmap:', error);
        showNotification('Erreur lors de la création de la heatmap', 'error');
    }
}

// ============================================================================
// Zone Englobante (Convex Hull)
// ============================================================================

function toggleConvexHull(folderId = null) {
    if (!state.map) return;

    const btn = document.getElementById('geo-convex-hull-toggle');

    // Si convex hull actif, le désactiver
    if (state.convexHullLayer) {
        state.map.removeLayer(state.convexHullLayer);
        state.convexHullLayer = null;
        state.convexHullEnabled = false;
        if (btn) btn.classList.remove('active');
        showNotification('Zone englobante désactivée', 'info');
        return;
    }

    // Récupérer les points visibles
    const hiddenFolderIds = new Set(
        state.folders
            .filter(f => f.is_visible === false || f.is_visible === 0)
            .map(f => f.id)
    );

    let points = state.points.filter(point => {
        if (!point.folder_id) return !folderId;
        if (folderId) return point.folder_id === folderId;
        return !hiddenFolderIds.has(point.folder_id);
    });

    if (points.length < 3) {
        showNotification('Minimum 3 points requis pour la zone englobante', 'warning');
        return;
    }

    // Calculer le convex hull
    const hullPoints = computeConvexHull(points.map(p => [p.lat, p.lng]));

    if (hullPoints.length < 3) {
        showNotification('Impossible de calculer la zone englobante', 'error');
        return;
    }

    // Trouver la couleur dominante
    const folderColors = {};
    points.forEach(p => {
        const folder = state.folders.find(f => f.id === p.folder_id);
        const color = folder ? folder.color : p.color || '#2563eb';
        folderColors[color] = (folderColors[color] || 0) + 1;
    });
    const dominantColor = Object.entries(folderColors).sort((a, b) => b[1] - a[1])[0]?.[0] || '#2563eb';

    // Créer le polygone
    state.convexHullLayer = L.polygon(hullPoints, {
        color: dominantColor,
        weight: 3,
        opacity: 0.8,
        fillColor: dominantColor,
        fillOpacity: 0.15,
        dashArray: '5, 10',
    }).addTo(state.map);

    // Ajouter un popup avec les infos
    const areaKm2 = calculatePolygonArea(hullPoints);
    state.convexHullLayer.bindPopup(`
        <div class="geo-popup">
            <strong><i class="fas fa-draw-polygon"></i> Zone englobante</strong>
            <p><strong>${points.length}</strong> points</p>
            <p>Surface: <strong>${areaKm2.toFixed(2)}</strong> km²</p>
        </div>
    `);

    state.convexHullEnabled = true;
    if (btn) btn.classList.add('active');
    showNotification(`Zone englobante créée (${points.length} points, ${areaKm2.toFixed(1)} km²)`, 'success');
}

// Algorithme de Graham Scan pour le convex hull
function computeConvexHull(points) {
    if (points.length < 3) return points;

    // Trouver le point le plus bas (et le plus à gauche en cas d'égalité)
    let lowestIdx = 0;
    for (let i = 1; i < points.length; i++) {
        if (points[i][0] < points[lowestIdx][0] ||
            (points[i][0] === points[lowestIdx][0] && points[i][1] < points[lowestIdx][1])) {
            lowestIdx = i;
        }
    }

    // Mettre le point le plus bas en premier
    [points[0], points[lowestIdx]] = [points[lowestIdx], points[0]];
    const pivot = points[0];

    // Trier par angle polaire
    const sorted = points.slice(1).sort((a, b) => {
        const angleA = Math.atan2(a[0] - pivot[0], a[1] - pivot[1]);
        const angleB = Math.atan2(b[0] - pivot[0], b[1] - pivot[1]);
        if (angleA === angleB) {
            // Trier par distance si même angle
            const distA = Math.hypot(a[0] - pivot[0], a[1] - pivot[1]);
            const distB = Math.hypot(b[0] - pivot[0], b[1] - pivot[1]);
            return distA - distB;
        }
        return angleA - angleB;
    });

    // Graham scan
    const stack = [pivot, sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
        while (stack.length > 1 && crossProduct(stack[stack.length - 2], stack[stack.length - 1], sorted[i]) <= 0) {
            stack.pop();
        }
        stack.push(sorted[i]);
    }

    return stack;
}

function crossProduct(o, a, b) {
    return (a[1] - o[1]) * (b[0] - o[0]) - (a[0] - o[0]) * (b[1] - o[1]);
}

// Calcul de la surface d'un polygone en km²
function calculatePolygonArea(coords) {
    if (coords.length < 3) return 0;

    // Formule de Shoelace avec conversion en km
    const R = 6371; // Rayon de la Terre en km

    // Convertir en radians et calculer la surface
    let area = 0;
    for (let i = 0; i < coords.length; i++) {
        const j = (i + 1) % coords.length;
        const lat1 = coords[i][0] * Math.PI / 180;
        const lat2 = coords[j][0] * Math.PI / 180;
        const dLng = (coords[j][1] - coords[i][1]) * Math.PI / 180;

        area += dLng * (2 + Math.sin(lat1) + Math.sin(lat2));
    }

    area = Math.abs(area * R * R / 2);
    return area;
}

function showConvexHullModal() {
    const modalHtml = `
        <div class="geo-modal active" id="convexHullModal">
            <div class="geo-modal-backdrop" onclick="GeoManager.closeModal('convexHullModal')"></div>
            <div class="geo-modal-content" style="max-width: 450px;">
                <div class="geo-modal-header">
                    <h3><i class="fas fa-draw-polygon"></i> Zone englobante</h3>
                    <button class="geo-modal-close" onclick="GeoManager.closeModal('convexHullModal')">&times;</button>
                </div>
                <div class="geo-modal-body">
                    <p class="text-gray-600 mb-3">Créez une zone englobante (convex hull) autour de vos points.</p>
                    <div class="form-group">
                        <label>Appliquer à</label>
                        <select id="convex-hull-folder" class="form-control">
                            <option value="">Tous les points visibles</option>
                            ${state.folders.map(f => `<option value="${f.id}">${escapeHtml(f.name)} (${state.points.filter(p => p.folder_id === f.id).length} points)</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-actions">
                        <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal('convexHullModal')">Annuler</button>
                        <button type="button" class="btn btn-primary" onclick="GeoManager.applyConvexHull()">
                            <i class="fas fa-draw-polygon"></i> Créer la zone
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
}

function applyConvexHull() {
    const folderId = document.getElementById('convex-hull-folder')?.value || null;
    closeModal('convexHullModal');
    toggleConvexHull(folderId);
}

// ============================================================================
// Légende Dynamique
// ============================================================================

function toggleLegend() {
    const btn = document.getElementById('geo-legend-toggle');

    // Si légende existe, la supprimer
    if (state.legendControl) {
        state.map.removeControl(state.legendControl);
        state.legendControl = null;
        if (btn) btn.classList.remove('active');
        showNotification('Légende masquée', 'info');
        return;
    }

    // Créer la légende
    state.legendControl = L.control({ position: 'bottomleft' });

    state.legendControl.onAdd = function() {
        const div = L.DomUtil.create('div', 'geo-legend');

        // Grouper les points par dossier
        const folderStats = {};
        const noFolderPoints = [];

        state.points.forEach(point => {
            if (point.folder_id) {
                if (!folderStats[point.folder_id]) {
                    const folder = state.folders.find(f => f.id === point.folder_id);
                    folderStats[point.folder_id] = {
                        name: folder ? folder.name : 'Sans dossier',
                        color: folder ? folder.color : '#94a3b8',
                        count: 0,
                        visible: folder ? (folder.is_visible !== false && folder.is_visible !== 0) : true
                    };
                }
                folderStats[point.folder_id].count++;
            } else {
                noFolderPoints.push(point);
            }
        });

        let html = `
            <div class="geo-legend-header">
                <i class="fas fa-list-ul"></i> Légende
            </div>
            <div class="geo-legend-content">
        `;

        // Dossiers
        Object.values(folderStats).forEach(folder => {
            const visibilityIcon = folder.visible ? 'fa-eye' : 'fa-eye-slash';
            html += `
                <div class="geo-legend-item ${!folder.visible ? 'legend-hidden' : ''}">
                    <span class="legend-color" style="background: ${folder.color}"></span>
                    <span class="legend-label">${escapeHtml(folder.name)}</span>
                    <span class="legend-count">${folder.count}</span>
                    <i class="fas ${visibilityIcon} legend-visibility"></i>
                </div>
            `;
        });

        // Points sans dossier
        if (noFolderPoints.length > 0) {
            html += `
                <div class="geo-legend-item">
                    <span class="legend-color" style="background: #94a3b8"></span>
                    <span class="legend-label">Sans dossier</span>
                    <span class="legend-count">${noFolderPoints.length}</span>
                </div>
            `;
        }

        // Total
        html += `
                <div class="geo-legend-total">
                    <strong>Total: ${state.points.length} points</strong>
                </div>
            </div>
        `;

        div.innerHTML = html;

        // Empêcher les événements de carte quand on clique sur la légende
        L.DomEvent.disableClickPropagation(div);
        L.DomEvent.disableScrollPropagation(div);

        return div;
    };

    state.legendControl.addTo(state.map);
    if (btn) btn.classList.add('active');
    showNotification('Légende affichée', 'success');
}

function showCoverageModal() {
    // Bug #17 Fix: Modal améliorée avec génération de zone km/minutes pour un point
    const selectedPoint = state.selectedPoint ? state.points.find(p => p.id === state.selectedPoint) : null;

    const modalHtml = `
        <div class="geo-modal active" id="coverageModal">
            <div class="geo-modal-backdrop" onclick="GeoManager.closeModal('coverageModal')"></div>
            <div class="geo-modal-content" style="max-width: 550px;">
                <div class="geo-modal-header">
                    <h3><i class="fas fa-chart-pie"></i> Zone de chalandise</h3>
                    <button class="geo-modal-close" onclick="GeoManager.closeModal('coverageModal')">&times;</button>
                </div>
                <div class="geo-modal-body">
                    <!-- Bug #17: Section Génération de zone pour un point -->
                    <div class="coverage-section" style="background: #f8fafc; padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
                        <h5 style="margin-bottom: 1rem; color: #1e40af;"><i class="fas fa-map-marker-alt"></i> Générer une zone pour un point</h5>

                        <div class="form-group">
                            <label>Point sélectionné</label>
                            <select id="coverage-point" class="form-control">
                                <option value="">-- Sélectionner un point --</option>
                                ${state.points.map(p => `
                                    <option value="${p.id}" ${selectedPoint && p.id === selectedPoint.id ? 'selected' : ''}>
                                        ${escapeHtml(p.name)} (${p.lat.toFixed(4)}, ${p.lng.toFixed(4)})
                                    </option>
                                `).join('')}
                            </select>
                        </div>

                        <div class="form-group">
                            <label>Type de zone</label>
                            <div class="btn-group" style="display: flex; gap: 0.5rem;">
                                <button type="button" class="btn btn-outline-primary active" id="zone-type-minutes" onclick="GeoManager.toggleZoneType('minutes')" style="flex: 1;">
                                    <i class="fas fa-clock"></i> Minutes (isochrone)
                                </button>
                                <button type="button" class="btn btn-outline-primary" id="zone-type-km" onclick="GeoManager.toggleZoneType('km')" style="flex: 1;">
                                    <i class="fas fa-circle"></i> Kilomètres (cercle)
                                </button>
                            </div>
                        </div>

                        <div class="form-group" id="zone-value-group">
                            <label id="zone-value-label">Durée (minutes)</label>
                            <input type="number" id="coverage-zone-value" class="form-control" value="15" min="1" max="120">
                        </div>

                        <div class="form-group" id="zone-transport-group">
                            <label>Mode de transport</label>
                            <select id="coverage-transport" class="form-control">
                                <option value="driving-car">🚗 Voiture</option>
                                <option value="cycling-regular">🚲 Vélo</option>
                                <option value="foot-walking">🚶 À pied</option>
                            </select>
                        </div>

                        <div class="form-row" style="display: flex; gap: 1rem;">
                            <div class="form-group" style="flex: 1;">
                                <label>Couleur</label>
                                <input type="color" id="coverage-zone-color" class="form-control" value="#10b981" style="height: 40px;">
                            </div>
                            <div class="form-group" style="flex: 1;">
                                <label>Opacité</label>
                                <input type="range" id="coverage-zone-opacity" class="form-control" min="0.1" max="0.8" step="0.1" value="0.3">
                            </div>
                        </div>

                        <button type="button" class="btn btn-success" style="width: 100%;" onclick="GeoManager.generateZoneForPoint()">
                            <i class="fas fa-plus-circle"></i> Générer et associer la zone
                        </button>
                    </div>

                    <!-- Section calcul de couverture globale -->
                    <details style="background: #fefce8; padding: 1rem; border-radius: 8px; border: 1px solid #fde047;">
                        <summary style="cursor: pointer; font-weight: 500; color: #854d0e;">
                            <i class="fas fa-chart-bar"></i> Calcul de couverture globale (statistiques)
                        </summary>
                        <div style="margin-top: 1rem;">
                            <div class="form-group">
                                <label>Rayon de couverture (km)</label>
                                <input type="number" id="coverage-radius" class="form-control" value="10" min="1" max="100">
                            </div>
                            <div class="form-group">
                                <label>Dossier (optionnel)</label>
                                <select id="coverage-folder" class="form-control">
                                    <option value="">Tous les points</option>
                                    ${state.folders.map(f => `<option value="${f.id}">${escapeHtml(f.name)}</option>`).join('')}
                                </select>
                            </div>
                            <button type="button" class="btn btn-primary" onclick="GeoManager.calculateCoverage()">
                                <i class="fas fa-calculator"></i> Calculer statistiques
                            </button>
                            <div id="coverage-result" style="margin-top: 1rem;"></div>
                        </div>
                    </details>

                    <div class="form-actions" style="margin-top: 1rem;">
                        <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal('coverageModal')">Fermer</button>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // Initialiser l'affichage selon le type par défaut (minutes)
    toggleZoneType('minutes');
}

// Bug #17 Fix: Toggle entre km et minutes
function toggleZoneType(type) {
    const minutesBtn = document.getElementById('zone-type-minutes');
    const kmBtn = document.getElementById('zone-type-km');
    const valueLabel = document.getElementById('zone-value-label');
    const valueInput = document.getElementById('coverage-zone-value');
    const transportGroup = document.getElementById('zone-transport-group');

    if (type === 'minutes') {
        minutesBtn.classList.add('active');
        kmBtn.classList.remove('active');
        valueLabel.textContent = 'Durée (minutes)';
        valueInput.max = 120;
        valueInput.value = 15;
        transportGroup.style.display = 'block';
    } else {
        kmBtn.classList.add('active');
        minutesBtn.classList.remove('active');
        valueLabel.textContent = 'Rayon (km)';
        valueInput.max = 100;
        valueInput.value = 10;
        transportGroup.style.display = 'none';
    }

    state.coverageZoneType = type;
}

// Bug #17 Fix: Générer et associer une zone à un point
async function generateZoneForPoint() {
    const pointId = document.getElementById('coverage-point').value;
    if (!pointId) {
        showNotification('Veuillez sélectionner un point', 'warning');
        return;
    }

    const zoneType = state.coverageZoneType || 'minutes';
    const zoneValue = parseFloat(document.getElementById('coverage-zone-value').value);
    const zoneColor = document.getElementById('coverage-zone-color').value;
    const zoneOpacity = parseFloat(document.getElementById('coverage-zone-opacity').value);
    const zoneTransport = document.getElementById('coverage-transport').value;

    if (!zoneValue || zoneValue <= 0) {
        showNotification('Veuillez entrer une valeur valide', 'warning');
        return;
    }

    try {
        showNotification('Génération de la zone en cours...', 'info');

        // Mettre à jour le point avec les nouvelles infos de zone
        const response = await api.fetch(`/points/${pointId}`, {
            method: 'PUT',
            body: JSON.stringify({
                zone_type: zoneType,
                zone_value: zoneValue,
                zone_color: zoneColor,
                zone_opacity: zoneOpacity,
                zone_transport: zoneType === 'minutes' ? zoneTransport : null
            })
        });

        if (response.success || response.id) {
            // Supprimer l'ancienne zone si elle existe
            if (state.zones[pointId]) {
                state.zones[pointId].remove();
                delete state.zones[pointId];
            }

            // Mettre à jour le point dans state
            const pointIndex = state.points.findIndex(p => p.id === pointId);
            if (pointIndex !== -1) {
                state.points[pointIndex] = {
                    ...state.points[pointIndex],
                    zone_type: zoneType,
                    zone_value: zoneValue,
                    zone_color: zoneColor,
                    zone_opacity: zoneOpacity,
                    zone_transport: zoneType === 'minutes' ? zoneTransport : null
                };

                // Ajouter la nouvelle zone sur la carte
                await addZoneToMap(state.points[pointIndex]);
            }

            showNotification(`Zone de ${zoneValue} ${zoneType === 'minutes' ? 'min' : 'km'} générée et associée au point`, 'success');
            closeModal('coverageModal');
        } else {
            throw new Error(response.message || 'Erreur lors de la mise à jour');
        }
    } catch (error) {
        console.error('Erreur génération zone:', error);
        showNotification(`Erreur: ${error.message}`, 'error');
    }
}

async function calculateCoverage() {
    const radiusKm = parseFloat(document.getElementById('coverage-radius').value) || 10;
    const folderId = document.getElementById('coverage-folder').value || null;
    const resultContainer = document.getElementById('coverage-result');

    resultContainer.innerHTML = '<div class="loading-indicator"><i class="fas fa-spinner fa-spin"></i> Calcul en cours...</div>';

    try {
        const result = await api.getCoverage(folderId, radiusKm);

        if (result.success && result.coverage) {
            const c = result.coverage;
            // Bug #16 Fix: Protéger contre les valeurs undefined et utiliser les bons noms de champs
            const populationCovered = c.population_covered || 0;
            const areaCoveredKm2 = c.area_covered_km2 || 0;
            const coveragePercent = c.coverage_percent || c.percent || 0;
            const pointsCount = c.points_count || 0;
            const communesCovered = c.communes_covered || 0;

            resultContainer.innerHTML = `
                <div class="analysis-result">
                    <h5><i class="fas fa-chart-pie"></i> Résultats de couverture</h5>
                    <div class="analysis-stats">
                        <div class="analysis-stat-item">
                            <div class="label">Points analysés</div>
                            <div class="value">${pointsCount}</div>
                        </div>
                        <div class="analysis-stat-item">
                            <div class="label">Population couverte</div>
                            <div class="value">${populationCovered.toLocaleString()}</div>
                        </div>
                        <div class="analysis-stat-item">
                            <div class="label">Communes couvertes</div>
                            <div class="value">${communesCovered.toLocaleString()}</div>
                        </div>
                        <div class="analysis-stat-item">
                            <div class="label">Surface estimée</div>
                            <div class="value">${areaCoveredKm2.toLocaleString()}<span class="unit">km²</span></div>
                        </div>
                        <div class="analysis-stat-item">
                            <div class="label">Taux de couverture</div>
                            <div class="value">${coveragePercent.toFixed(1)}<span class="unit">%</span></div>
                        </div>
                    </div>
                </div>
            `;
        } else {
            resultContainer.innerHTML = `<p class="error-text">${result.message || 'Erreur de calcul'}</p>`;
        }
    } catch (error) {
        resultContainer.innerHTML = `<p class="error-text">Erreur: ${error.message}</p>`;
    }
}

// ============================================================================
// Export
// ============================================================================

function showExportModal() {
    const modalHtml = `
        <div class="geo-modal active" id="exportModal">
            <div class="geo-modal-backdrop" onclick="GeoManager.closeModal('exportModal')"></div>
            <div class="geo-modal-content" style="max-width: 500px;">
                <div class="geo-modal-header">
                    <h3><i class="fas fa-download"></i> Exporter</h3>
                    <button class="geo-modal-close" onclick="GeoManager.closeModal('exportModal')">&times;</button>
                </div>
                <div class="geo-modal-body">
                    <p style="color: #64748b; margin-bottom: 1rem;">Choisissez le format d'export :</p>
                    <div class="export-options">
                        <button class="export-option-btn" onclick="GeoManager.exportPNG()">
                            <i class="fas fa-image"></i>
                            <span>PNG</span>
                            <small>Image haute définition</small>
                        </button>
                        <button class="export-option-btn" onclick="GeoManager.exportPDF()">
                            <i class="fas fa-file-pdf"></i>
                            <span>PDF</span>
                            <small>Document imprimable</small>
                        </button>
                        <button class="export-option-btn" onclick="GeoManager.exportSVG()">
                            <i class="fas fa-vector-square"></i>
                            <span>SVG</span>
                            <small>Vectoriel éditable</small>
                        </button>
                        <button class="export-option-btn" onclick="GeoManager.exportGeoJSON()">
                            <i class="fas fa-code"></i>
                            <span>GeoJSON</span>
                            <small>Données géospatiales</small>
                        </button>
                        <button class="export-option-btn" onclick="GeoManager.exportKML()">
                            <i class="fas fa-globe"></i>
                            <span>KML</span>
                            <small>Google Earth</small>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
}

async function exportPNG() {
    closeModal('exportModal');
    showNotification('Génération de l\'image...', 'info');

    const mapContainer = document.getElementById('geo-map-embed') || document.getElementById('geo-map');
    if (!mapContainer) {
        showNotification('Carte non trouvée', 'error');
        return;
    }

    try {
        // Lazy-load html2canvas si nécessaire
        await ensureExportLibs();

        const canvas = await html2canvas(mapContainer, {
            useCORS: true,
            allowTaint: true,
            scale: 2, // HD
            logging: false,
        });

        // Télécharger
        const link = document.createElement('a');
        link.download = `geo-manager-export-${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();

        showNotification('Image PNG exportée', 'success');
    } catch (error) {
        console.error('Erreur export PNG:', error);
        showNotification('Erreur lors de l\'export PNG', 'error');
    }
}

async function exportPDF() {
    closeModal('exportModal');
    showNotification('Génération du PDF...', 'info');

    const mapContainer = document.getElementById('geo-map-embed') || document.getElementById('geo-map');
    if (!mapContainer) {
        showNotification('Carte non trouvée', 'error');
        return;
    }

    try {
        // Lazy-load export libs si nécessaire
        await ensureExportLibs();

        const canvas = await html2canvas(mapContainer, {
            useCORS: true,
            allowTaint: true,
            scale: 2,
            logging: false,
        });

        const { jsPDF } = jspdf;
        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        // Titre
        pdf.setFontSize(16);
        pdf.text('Géo Manager Export', 14, 15);
        pdf.setFontSize(10);
        pdf.text(new Date().toLocaleDateString('fr-FR'), 14, 22);

        // Image de la carte
        pdf.addImage(imgData, 'PNG', 10, 30, pdfWidth - 20, Math.min(pdfHeight, pdf.internal.pageSize.getHeight() - 40));

        pdf.save(`geo-manager-export-${Date.now()}.pdf`);
        showNotification('PDF exporté', 'success');
    } catch (error) {
        console.error('Erreur export PDF:', error);
        showNotification('Erreur lors de l\'export PDF', 'error');
    }
}

function exportGeoJSON() {
    closeModal('exportModal');
    const folderId = state.selectedFolder || null;
    const url = `/api/geo-manager/export/geojson${folderId ? '?folder_id=' + folderId : ''}`;
    window.open(url, '_blank');
    showNotification('Export GeoJSON téléchargé', 'success');
}

function exportKML() {
    closeModal('exportModal');
    const folderId = state.selectedFolder || null;
    const url = `/api/geo-manager/export/kml${folderId ? '?folder_id=' + folderId : ''}`;
    window.open(url, '_blank');
    showNotification('Export KML téléchargé', 'success');
}

function exportSVG() {
    closeModal('exportModal');

    if (state.points.length === 0) {
        showNotification('Aucun point à exporter', 'warning');
        return;
    }

    try {
        // Récupérer les bounds de la carte
        const bounds = state.map.getBounds();
        const svgWidth = 800;
        const svgHeight = 600;

        // Fonction pour convertir lat/lng en coordonnées SVG
        const latLngToSvg = (lat, lng) => {
            const x = ((lng - bounds.getWest()) / (bounds.getEast() - bounds.getWest())) * svgWidth;
            const y = ((bounds.getNorth() - lat) / (bounds.getNorth() - bounds.getSouth())) * svgHeight;
            return { x, y };
        };

        // Créer le SVG
        let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgWidth} ${svgHeight}" width="${svgWidth}" height="${svgHeight}">
  <defs>
<style>
  .point-marker { cursor: pointer; }
  .point-marker:hover { filter: brightness(1.2); }
  .point-label { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
  .zone { opacity: 0.3; }
  .zone-border { fill: none; stroke-width: 2; }
  .convex-hull { fill-opacity: 0.15; stroke-dasharray: 5,5; stroke-width: 2; }
</style>
  </defs>
  <title>Géo Manager - Export ${new Date().toLocaleDateString()}</title>

  <!-- Fond -->
  <rect width="100%" height="100%" fill="#f0f9ff"/>

  <!-- Zones de chalandise -->
  <g id="zones">
`;

        // Ajouter les zones (cercles pour les zones en km)
        state.points
            .filter(p => p.zone_type === 'km' && p.zone_value)
            .forEach(point => {
                const { x, y } = latLngToSvg(point.lat, point.lng);
                // Approximation du rayon en pixels
                const radiusDeg = point.zone_value / 111; // km to degrees
                const radiusPx = (radiusDeg / (bounds.getEast() - bounds.getWest())) * svgWidth;

                svgContent += `    <circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="${radiusPx.toFixed(2)}"
  fill="${point.zone_color || '#10b981'}" class="zone" stroke="${point.zone_color || '#10b981'}" stroke-opacity="0.5"/>
`;
            });

        svgContent += `  </g>

  <!-- Convex Hull (si actif) -->
  <g id="convex-hull">
`;

        // Ajouter le convex hull s'il existe
        if (state.convexHullLayer && state.convexHullEnabled) {
            const latlngs = state.convexHullLayer.getLatLngs()[0];
            if (latlngs && latlngs.length > 0) {
                const pathPoints = latlngs.map(ll => {
                    const { x, y } = latLngToSvg(ll.lat, ll.lng);
                    return `${x.toFixed(2)},${y.toFixed(2)}`;
                });
                svgContent += `    <polygon points="${pathPoints.join(' ')}" fill="#2563eb" stroke="#2563eb" class="convex-hull"/>
`;
            }
        }

        svgContent += `  </g>

  <!-- Points -->
  <g id="points">
`;

        // Ajouter les points
        state.points.forEach((point, index) => {
            const { x, y } = latLngToSvg(point.lat, point.lng);
            const color = point.color || '#2563eb';

            svgContent += `    <g class="point-marker" id="point-${index}">
  <circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="8" fill="${color}" stroke="white" stroke-width="2"/>
  <text x="${(x + 12).toFixed(2)}" y="${(y + 4).toFixed(2)}" class="point-label">${escapeHtml(point.name)}</text>
</g>
`;
        });

        svgContent += `  </g>

  <!-- Légende -->
  <g id="legend" transform="translate(10, ${svgHeight - 60})">
<rect width="180" height="50" fill="white" stroke="#e2e8f0" rx="5"/>
<text x="10" y="20" font-weight="bold" font-size="11">Géo Manager</text>
<text x="10" y="35" font-size="9" fill="#64748b">${state.points.length} points</text>
<text x="10" y="45" font-size="8" fill="#94a3b8">${new Date().toLocaleDateString('fr-FR')}</text>
  </g>
</svg>`;

        // Créer et télécharger le fichier
        const blob = new Blob([svgContent], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `geo-manager-${Date.now()}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        showNotification('Export SVG téléchargé', 'success');
    } catch (error) {
        console.error('Erreur export SVG:', error);
        showNotification("Erreur lors de l'export SVG", 'error');
    }
}

// ============================================================================
// Itinéraires
// ============================================================================

let routeCapabilities = null;
let lastRouteResult = null;

async function showRouteModal() {
    // Filtrer les points visibles
    const visiblePoints = state.selectedFolder
        ? state.points.filter(p => p.folder_id === state.selectedFolder)
        : state.points;

    if (visiblePoints.length < 2) {
        showNotification('Minimum 2 points requis pour un itinéraire', 'warning');
        return;
    }

    try {
        const catalog = await api.getAnalysisCatalog();
        routeCapabilities = catalog.route_optimization;
    } catch (error) {
        showNotification(error.message || 'Limites du service d’itinéraire indisponibles', 'error');
        return;
    }
    const maximum = Number(routeCapabilities?.max_points);
    const roundTripMaximum = Number(routeCapabilities?.max_points_return_to_start);
    if (!Number.isInteger(maximum) || !Number.isInteger(roundTripMaximum)) {
        showNotification('Limites du service d’itinéraire indisponibles', 'error');
        return;
    }
    if (visiblePoints.length > maximum) {
        showNotification(
            `Ce périmètre contient ${visiblePoints.length} points. Le service accepte ${maximum} points au maximum.`,
            'warning',
        );
        return;
    }

    const modalHtml = `
        <div class="geo-modal active" id="routeModal">
            <div class="geo-modal-backdrop" onclick="GeoManager.closeModal('routeModal')"></div>
            <div class="geo-modal-content" style="max-width: 550px;">
                <div class="geo-modal-header">
                    <h3><i class="fas fa-route"></i> Optimiser un itinéraire</h3>
                    <button class="geo-modal-close" onclick="GeoManager.closeModal('routeModal')">&times;</button>
                </div>
                <div class="geo-modal-body">
                    <p style="color: #64748b; margin-bottom: 1rem;">
                        Sélectionnez les points à inclure dans l'itinéraire :
                    </p>
                    <div class="route-points-list" style="max-height: 250px; overflow-y: auto; margin-bottom: 1rem;">
                        ${visiblePoints.map(p => `
                            <label class="route-point-item">
                                <input type="checkbox" value="${p.id}" checked>
                                <span style="color: ${p.color}"><i class="fas fa-map-marker-alt"></i></span>
                                <span>${escapeHtml(p.name)}</span>
                            </label>
                        `).join('')}
                    </div>
                    <div class="form-group">
                        <label>Point de départ</label>
                        <select id="route-start" class="form-control">
                            ${visiblePoints.map(p => `<option value="${p.id}">${escapeHtml(p.name)}</option>`).join('')}
                        </select>
                        <small>Le départ est explicite et doit rester dans la sélection.</small>
                    </div>
                    <div class="form-group">
                        <label class="checkbox-label">
                            <input type="checkbox" id="route-return">
                            Retour au point de départ (maximum ${roundTripMaximum} points)
                        </label>
                    </div>
                    <div id="route-result" style="display: none;"></div>
                    <div class="form-actions">
                        <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal('routeModal')">Fermer</button>
                        <button type="button" class="btn btn-primary" onclick="GeoManager.optimizeRoute()">
                            <i class="fas fa-magic"></i> Optimiser
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
}

async function optimizeRoute() {
    const checkboxes = document.querySelectorAll('.route-points-list input[type="checkbox"]:checked');
    const pointIds = Array.from(checkboxes).map(cb => cb.value);
    const startPointId = document.getElementById('route-start').value || null;
    const returnToStart = document.getElementById('route-return').checked;
    const resultContainer = document.getElementById('route-result');

    if (pointIds.length < 2) {
        showNotification('Sélectionnez au moins 2 points', 'warning');
        return;
    }
    const maximum = returnToStart
        ? Number(routeCapabilities?.max_points_return_to_start)
        : Number(routeCapabilities?.max_points);
    if (!Number.isInteger(maximum) || pointIds.length > maximum) {
        showNotification(`Sélectionnez au maximum ${maximum || 0} points pour ce parcours`, 'warning');
        return;
    }
    if (!startPointId || !pointIds.includes(startPointId)) {
        showNotification('Le point de départ doit faire partie de la sélection', 'warning');
        return;
    }

    resultContainer.innerHTML = '<div class="loading-indicator"><i class="fas fa-spinner fa-spin"></i> Optimisation...</div>';
    resultContainer.style.display = 'block';

    try {
        const result = await api.optimizeRoute(pointIds, startPointId, returnToStart);

        if (result.success && result.route) {
            const r = result.route;
            lastRouteResult = r;
            resultContainer.innerHTML = `
                <div class="analysis-result">
                    <h5><i class="fas fa-route"></i> Itinéraire optimisé</h5>
                    <div class="analysis-stats">
                        <div class="analysis-stat-item">
                            <div class="label">Fournisseur</div>
                            <div class="value">${escapeHtml(r.provider === 'valhalla' ? 'Valhalla' : r.provider)}</div>
                        </div>
                        <div class="analysis-stat-item">
                            <div class="label">Distance routière</div>
                            <div class="value">${r.total_distance_km}<span class="unit">km</span></div>
                        </div>
                        <div class="analysis-stat-item">
                            <div class="label">Durée routière</div>
                            <div class="value">${Math.floor(r.total_duration_seconds / 3600)}h${Math.round((r.total_duration_seconds % 3600) / 60).toString().padStart(2, '0')}</div>
                        </div>
                    </div>
                    <div class="route-order" style="margin-top: 0.75rem;">
                        <small style="color: #64748b;">Ordre optimisé :</small>
                        <ol style="margin: 0.5rem 0 0 1rem; padding: 0; font-size: 0.85rem;">
                            ${r.points.map(p => `<li>${escapeHtml(p.name)}</li>`).join('')}
                            ${r.return_to_start ? `<li><i>Retour à ${escapeHtml(r.points[0].name)}</i></li>` : ''}
                        </ol>
                    </div>
                    <button class="btn btn-sm btn-primary" style="margin-top: 0.75rem; width: 100%;"
                        onclick="GeoManager.drawLastRoute(); GeoManager.closeModal('routeModal');">
                        <i class="fas fa-route"></i> Afficher le tracé routier
                    </button>
                </div>
            `;
        } else {
            resultContainer.innerHTML = `<p class="error-text">${result.message || 'Erreur d\'optimisation'}</p>`;
        }
    } catch (error) {
        resultContainer.innerHTML = `<p class="error-text">Erreur: ${error.message}</p>`;
    }
}

function drawRoute(route) {
    if (!state.map) return;
    const points = Array.isArray(route?.points) ? route.points : [];
    const coordinates = route?.geometry?.type === 'LineString'
        ? route.geometry.coordinates
        : [];
    if (points.length < 2 || coordinates.length < 2) {
        showNotification('Le fournisseur n’a retourné aucun tracé routier exploitable', 'error');
        return;
    }

    // Supprimer l'ancien tracé
    clearRoute();

    // La géométrie [longitude, latitude] vient du fournisseur.
    const latlngs = coordinates.map(([lng, lat]) => [lat, lng]);

    state.routePolyline = L.polyline(latlngs, {
        color: '#2563eb',
        weight: 4,
        opacity: 0.8,
    }).addTo(state.map);

    // Ajouter des numéros aux points
    points.forEach((p, i) => {
        const marker = L.marker([p.lat, p.lng], {
            icon: L.divIcon({
                className: 'route-number-icon',
                html: `<div class="route-number">${i + 1}</div>`,
                iconSize: [24, 24],
                iconAnchor: [12, 12],
            })
        }).addTo(state.map);
        state.routeMarkers.push(marker);
    });

    // Ajuster la vue
    state.map.fitBounds(state.routePolyline.getBounds(), { padding: [30, 30] });

    showNotification('Itinéraire affiché sur la carte', 'success');
}

function drawLastRoute() {
    drawRoute(lastRouteResult);
}

function clearRoute() {
    if (state.routePolyline) {
        state.map.removeLayer(state.routePolyline);
        state.routePolyline = null;
    }
    state.routeMarkers.forEach(marker => state.map?.removeLayer(marker));
    state.routeMarkers = [];
}

// ============================================================================
// Partage
// ============================================================================

function showShareModal(folderId = null) {
    const folder = folderId ? state.folders.find(f => f.id === folderId) : null;
    const folderName = folder ? folder.name : 'Tous les points';

    const modalHtml = `
        <div class="geo-modal active" id="shareModal">
            <div class="geo-modal-backdrop" onclick="GeoManager.closeModal('shareModal')"></div>
            <div class="geo-modal-content" style="max-width: 500px;">
                <div class="geo-modal-header">
                    <h3><i class="fas fa-share-alt"></i> Partager "${escapeHtml(folderName)}"</h3>
                    <button class="geo-modal-close" onclick="GeoManager.closeModal('shareModal')">&times;</button>
                </div>
                <div class="geo-modal-body">
                    <p>Ce lien est strictement en lecture seule. Les contacts, notes et données CRM ne sont pas partagés.</p>
                    <div class="form-group">
                        <label>Expiration (optionnel)</label>
                        <input type="date" id="share-expires" class="form-control" min="${new Date().toISOString().split('T')[0]}">
                    </div>
                    <div id="share-result" style="margin-top: 1rem; display: none;">
                        <label>Lien de partage</label>
                        <div class="share-link-container">
                            <input type="text" id="share-link" class="form-control" readonly>
                            <button class="btn btn-sm" onclick="GeoManager.copyShareLink()">
                                <i class="fas fa-copy"></i>
                            </button>
                        </div>
                    </div>
                    <div class="form-actions">
                        <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal('shareModal')">Fermer</button>
                        <button type="button" class="btn btn-primary" onclick="GeoManager.createShare('${folderId || ''}')">
                            <i class="fas fa-link"></i> Générer le lien
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
}

async function createShare(folderId) {
    const expiresInput = document.getElementById('share-expires').value;
    const expires = expiresInput ? new Date(expiresInput).toISOString() : null;

    try {
        const result = await api.fetch('/shares', {
            method: 'POST',
            body: JSON.stringify({
                folder_id: folderId || null,
                permissions: 'view',
                expires_at: expires,
            }),
        });

        if (result.success && result.share) {
            const shareUrl = result.share_url;
            document.getElementById('share-link').value = shareUrl;
            document.getElementById('share-result').style.display = 'block';
            showNotification('Lien de partage créé', 'success');
        } else {
            showNotification(result.message || 'Erreur de création', 'error');
        }
    } catch (error) {
        showNotification('Erreur: ' + error.message, 'error');
    }
}

function copyShareLink() {
    const input = document.getElementById('share-link');
    if (input && input.value) {
        navigator.clipboard.writeText(input.value).then(() => {
            showNotification('Lien copié dans le presse-papier', 'success');
        }).catch(() => {
            // Fallback
            input.select();
            document.execCommand('copy');
            showNotification('Lien copié', 'success');
        });
    }
}

// ============================================================================
// Modals
// ============================================================================

function showCreateFolderModal(parentId = null) {
    const modal = createModal('Nouveau dossier', `
        <form id="create-folder-form">
            <div class="form-group">
                <label>Nom du dossier</label>
                <input type="text" name="name" required placeholder="Ex: Réseau IDF" class="form-control">
            </div>
            <div class="form-group">
                <label>Couleur</label>
                <input type="color" name="color" value="${config.defaultColor}" class="form-control">
            </div>
            <div class="form-group">
                <label>Icône</label>
                <select name="icon" class="form-control">
                    ${Object.entries(config.folderIcons).map(([key, icon]) =>
                        `<option value="${key}">${key}</option>`
                    ).join('')}
                </select>
            </div>
            ${parentId ? `<input type="hidden" name="parent_id" value="${parentId}">` : ''}
            <div class="form-actions">
                <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                <button type="submit" class="btn btn-primary">Créer</button>
            </div>
        </form>
    `);

    document.getElementById('create-folder-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);

        try {
            await api.createFolder({
                name: formData.get('name'),
                color: formData.get('color'),
                icon: formData.get('icon'),
                parent_id: formData.get('parent_id') || null,
            });

            closeModal();
            await loadData();
            showNotification('Dossier créé', 'success');
        } catch (error) {
            showNotification(error.message, 'error');
        }
    });
}

function showEditFolderModal(folderId) {
    const folder = state.folders.find(f => f.id === folderId);
    if (!folder) return;

    const modal = createModal('Modifier le dossier', `
        <form id="edit-folder-form">
            <div class="form-group">
                <label>Nom du dossier</label>
                <input type="text" name="name" value="${escapeHtml(folder.name)}" required class="form-control">
            </div>
            <div class="form-group">
                <label>Couleur</label>
                <input type="color" name="color" value="${folder.color}" class="form-control">
            </div>
            <div class="form-group">
                <label>Icône</label>
                <select name="icon" class="form-control">
                    ${Object.entries(config.folderIcons).map(([key, icon]) =>
                        `<option value="${key}" ${folder.icon === key ? 'selected' : ''}>${key}</option>`
                    ).join('')}
                </select>
            </div>
            <div class="form-actions">
                <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                <button type="submit" class="btn btn-primary">Enregistrer</button>
            </div>
        </form>
    `);

    document.getElementById('edit-folder-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);

        try {
            await api.updateFolder(folderId, {
                name: formData.get('name'),
                color: formData.get('color'),
                icon: formData.get('icon'),
            });

            closeModal();
            await loadData();
            showNotification('Dossier modifié', 'success');
        } catch (error) {
            showNotification(error.message, 'error');
        }
    });
}

function showQuickAddPointModal(lat, lng) {
    const modal = createModal('Ajouter un point', `
        <form id="quick-add-point-form">
            <div class="form-group">
                <label>Nom</label>
                <input type="text" name="name" required placeholder="Ex: Magasin Paris" class="form-control">
            </div>
            <div class="form-row">
                <div class="form-group">
                    <label>Latitude</label>
                    <input type="number" name="lat" value="${lat.toFixed(6)}" step="0.000001" required class="form-control">
                </div>
                <div class="form-group">
                    <label>Longitude</label>
                    <input type="number" name="lng" value="${lng.toFixed(6)}" step="0.000001" required class="form-control">
                </div>
            </div>
            <div class="form-group">
                <label>Dossier</label>
                <select name="folder_id" class="form-control">
                    <option value="">-- Aucun --</option>
                    ${state.folders.map(f =>
                        `<option value="${f.id}" ${f.id === state.selectedFolder ? 'selected' : ''}>${escapeHtml(f.name)}</option>`
                    ).join('')}
                </select>
            </div>
            <div class="form-group">
                <label>Couleur</label>
                <input type="color" name="color" value="${config.defaultColor}" class="form-control" onchange="GeoManager.updateIconColors(this.value)">
            </div>
            <div class="form-group">
                <label>Icône</label>
                ${buildIconPicker('default', config.defaultColor)}
            </div>

            <h4 class="mt-4">Zone de chalandise</h4>
            <div class="form-row">
                <div class="form-group">
                    <label>Type</label>
                    <select name="zone_type" class="form-control" onchange="document.getElementById('quick-zone-value-group').style.display = this.value === 'none' ? 'none' : 'block'; document.getElementById('quick-zone-style-group').style.display = this.value === 'none' ? 'none' : 'flex'; document.getElementById('quick-zone-transport-group').style.display = this.value === 'minutes' ? 'block' : 'none';">
                        <option value="none" selected>Aucune</option>
                        <option value="km">Kilomètres (rayon)</option>
                        <option value="minutes">Minutes (isochrone)</option>
                    </select>
                </div>
                <div class="form-group" id="quick-zone-value-group" style="display: none">
                    <label>Valeur</label>
                    <input type="number" name="zone_value" value="5" min="1" max="120" class="form-control">
                </div>
            </div>
            <div class="form-row" id="quick-zone-style-group" style="display: none">
                <div class="form-group">
                    <label>Couleur zone</label>
                    <input type="color" name="zone_color" value="${config.defaultZoneColor}" class="form-control">
                </div>
                <div class="form-group">
                    <label>Opacité</label>
                    <input type="range" name="zone_opacity" value="30" min="10" max="80" class="form-control">
                </div>
            </div>
            <div class="form-group" id="quick-zone-transport-group" style="display: none">
                <label>Mode de transport</label>
                <select name="zone_transport" class="form-control">
                    <option value="driving-car">Voiture</option>
                    <option value="foot-walking">À pied</option>
                    <option value="cycling-regular">Vélo</option>
                </select>
            </div>

            <div class="form-actions">
                <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                <button type="submit" class="btn btn-primary">Ajouter</button>
            </div>
        </form>
    `);

    document.getElementById('quick-add-point-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const zoneType = formData.get('zone_type');

        try {
            await api.createPoint({
                name: formData.get('name'),
                lat: parseFloat(formData.get('lat')),
                lng: parseFloat(formData.get('lng')),
                folder_id: formData.get('folder_id') || null,
                color: formData.get('color'),
                icon_type: formData.get('icon_type') || 'preset',
                icon_value: formData.get('icon_value') || 'default',
                zone_type: zoneType,
                zone_value: zoneType !== 'none' ? parseFloat(formData.get('zone_value')) : null,
                zone_color: formData.get('zone_color'),
                zone_opacity: parseFloat(formData.get('zone_opacity')) / 100,
                zone_transport: formData.get('zone_transport') || 'driving-car',
            });

            // Audit Fix B1: Reset le formulaire avant de fermer
            e.target.reset();
            closeModal();
            await loadData();
            showNotification('Point ajouté', 'success');
        } catch (error) {
            showNotification(error.message, 'error');
        }
    });
}

function showEditPointModal(pointId) {
    const point = state.points.find(p => p.id === pointId);
    if (!point) return;

    const modal = createModal('Modifier le point', `
        <form id="edit-point-form">
            <div class="form-group">
                <label>Nom</label>
                <input type="text" name="name" value="${escapeHtml(point.name)}" required class="form-control">
            </div>
            <div class="form-group">
                <label>Description</label>
                <textarea name="description" class="form-control" rows="2">${escapeHtml(point.description || '')}</textarea>
            </div>
            <div class="form-row">
                <div class="form-group">
                    <label>Latitude</label>
                    <input type="number" name="lat" value="${point.lat}" step="0.000001" required class="form-control">
                </div>
                <div class="form-group">
                    <label>Longitude</label>
                    <input type="number" name="lng" value="${point.lng}" step="0.000001" required class="form-control">
                </div>
            </div>
            <div class="form-group">
                <label>Dossier</label>
                <select name="folder_id" class="form-control">
                    <option value="">-- Aucun --</option>
                    ${state.folders.map(f =>
                        `<option value="${f.id}" ${f.id === point.folder_id ? 'selected' : ''}>${escapeHtml(f.name)}</option>`
                    ).join('')}
                </select>
            </div>
            <div class="form-group">
                <label>Couleur</label>
                <input type="color" name="color" value="${point.color || config.defaultColor}" class="form-control" onchange="GeoManager.updateIconColors(this.value)">
            </div>
            <div class="form-group">
                <label>Icône</label>
                ${buildIconPicker(point.icon_value || 'default', point.color || config.defaultColor, point.icon_type || 'preset')}
            </div>

            <h4 class="mt-4">Zone de chalandise</h4>
            <div class="form-row">
                <div class="form-group">
                    <label>Type</label>
                    <select name="zone_type" class="form-control" onchange="document.getElementById('zone-value-group').style.display = this.value === 'none' ? 'none' : 'block'; document.getElementById('zone-style-group').style.display = this.value === 'none' ? 'none' : 'flex'; document.getElementById('zone-transport-group').style.display = this.value === 'minutes' ? 'block' : 'none';">
                        <option value="none" ${point.zone_type === 'none' || !point.zone_type ? 'selected' : ''}>Aucune</option>
                        <option value="km" ${point.zone_type === 'km' ? 'selected' : ''}>Kilomètres (rayon)</option>
                        <option value="minutes" ${point.zone_type === 'minutes' ? 'selected' : ''}>Minutes (isochrone)</option>
                    </select>
                </div>
                <div class="form-group" id="zone-value-group" style="display: ${point.zone_type === 'none' || !point.zone_type ? 'none' : 'block'}">
                    <label>Valeur</label>
                    <input type="number" name="zone_value" value="${point.zone_value || 5}" min="1" max="120" class="form-control">
                </div>
            </div>
            <div class="form-row" id="zone-style-group" style="display: ${point.zone_type === 'none' || !point.zone_type ? 'none' : 'flex'}">
                <div class="form-group">
                    <label>Couleur zone</label>
                    <input type="color" name="zone_color" value="${point.zone_color || config.defaultZoneColor}" class="form-control">
                </div>
                <div class="form-group">
                    <label>Opacité</label>
                    <input type="range" name="zone_opacity" value="${(point.zone_opacity || config.defaultZoneOpacity) * 100}" min="10" max="80" class="form-control">
                </div>
            </div>
            <div class="form-group" id="zone-transport-group" style="display: ${point.zone_type === 'minutes' ? 'block' : 'none'}">
                <label>Mode de transport</label>
                <select name="zone_transport" class="form-control">
                    <option value="driving-car" ${!point.zone_transport || point.zone_transport === 'driving-car' ? 'selected' : ''}>Voiture</option>
                    <option value="foot-walking" ${point.zone_transport === 'foot-walking' ? 'selected' : ''}>À pied</option>
                    <option value="cycling-regular" ${point.zone_transport === 'cycling-regular' ? 'selected' : ''}>Vélo</option>
                </select>
            </div>

            <div class="form-actions">
                <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                <button type="submit" class="btn btn-primary">Enregistrer</button>
            </div>
        </form>
    `);

    // Toggle zone style visibility
    document.querySelector('[name="zone_type"]').addEventListener('change', function() {
        document.getElementById('zone-value-group').style.display = this.value === 'none' ? 'none' : 'block';
        document.getElementById('zone-style-group').style.display = this.value === 'none' ? 'none' : 'flex';
        document.getElementById('zone-transport-group').style.display = this.value === 'minutes' ? 'block' : 'none';
    });

    document.getElementById('edit-point-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const zoneType = formData.get('zone_type');

        try {
            await api.updatePoint(pointId, {
                name: formData.get('name'),
                description: formData.get('description'),
                lat: parseFloat(formData.get('lat')),
                lng: parseFloat(formData.get('lng')),
                folder_id: formData.get('folder_id') || null,
                color: formData.get('color'),
                icon_type: formData.get('icon_type') || 'preset',
                icon_value: formData.get('icon_value'),
                zone_type: zoneType,
                zone_value: zoneType !== 'none' ? parseFloat(formData.get('zone_value')) : null,
                zone_color: formData.get('zone_color'),
                zone_opacity: parseFloat(formData.get('zone_opacity')) / 100,
                zone_transport: formData.get('zone_transport') || 'driving-car',
            });

            // Audit Fix B1: Reset le formulaire avant de fermer
            e.target.reset();
            closeModal();
            await loadData();
            showNotification('Point modifié', 'success');
        } catch (error) {
            showNotification(error.message, 'error');
        }
    });
}

function showImportModal() {
    const modal = createModal('Importer des points', `
        <div class="import-tabs">
            <!-- Audit Fix B2: Ajout data-tab pour sélecteur robuste -->
            <button class="import-tab active" data-tab="coords" onclick="GeoManager.switchImportTab('coords')"><i class="fas fa-keyboard"></i> Texte</button>
            <button class="import-tab" data-tab="csv" onclick="GeoManager.switchImportTab('csv')"><i class="fas fa-file-csv"></i> CSV/Excel</button>
            <button class="import-tab" data-tab="kml" onclick="GeoManager.switchImportTab('kml')"><i class="fas fa-map"></i> KML</button>
            <button class="import-tab" data-tab="gmaps" onclick="GeoManager.switchImportTab('gmaps')"><i class="fab fa-google"></i> Maps</button>
        </div>

        <div id="import-tab-coords" class="import-tab-content active">
            <form id="import-coords-form">
                <div class="form-group">
                    <label>Collez vos coordonnées (une par ligne)</label>
                    <textarea name="text" rows="6" class="form-control" placeholder="48.8566, 2.3522&#10;45.764, 4.8357&#10;Mon Point|43.2965, 5.3698"></textarea>
                    <small class="text-gray-500">Formats: lat,lng ou lng,lat ou Nom|lat,lng</small>
                </div>
                <div class="form-group">
                    <label>Dossier destination</label>
                    <select name="folder_id" class="form-control">
                        <option value="">-- Aucun --</option>
                        ${state.folders.map(f => `<option value="${f.id}">${escapeHtml(f.name)}</option>`).join('')}
                    </select>
                </div>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-upload"></i> Importer</button>
                </div>
            </form>
        </div>

        <div id="import-tab-csv" class="import-tab-content">
            <form id="import-csv-form">
                <div class="form-group">
                    <label>Fichier CSV ou Excel (.csv, .xlsx, .xls)</label>
                    <input type="file" name="file" accept=".csv,.xlsx,.xls" class="form-control" id="csv-file-input">
                </div>
                <div class="form-group csv-preview-group" style="display:none;">
                    <label>Aperçu des données</label>
                    <div id="csv-preview" class="csv-preview"></div>
                </div>
                <div class="form-group csv-mapping-group" style="display:none;">
                    <label>Correspondance des colonnes</label>
                    <div class="form-row">
                        <div class="form-group">
                            <label>Nom</label>
                            <select name="col_name" class="form-control" id="csv-col-name"></select>
                        </div>
                        <div class="form-group">
                            <label>Latitude</label>
                            <select name="col_lat" class="form-control" id="csv-col-lat"></select>
                        </div>
                    </div>
                    <div class="form-row">
                        <div class="form-group">
                            <label>Longitude</label>
                            <select name="col_lng" class="form-control" id="csv-col-lng"></select>
                        </div>
                        <div class="form-group">
                            <label>Description (optionnel)</label>
                            <select name="col_desc" class="form-control" id="csv-col-desc">
                                <option value="">-- Aucune --</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label>Dossier destination</label>
                    <select name="folder_id" class="form-control">
                        <option value="">-- Aucun --</option>
                        ${state.folders.map(f => `<option value="${f.id}">${escapeHtml(f.name)}</option>`).join('')}
                    </select>
                </div>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                    <button type="submit" class="btn btn-primary" id="csv-import-btn" disabled><i class="fas fa-upload"></i> Importer</button>
                </div>
            </form>
        </div>

        <div id="import-tab-kml" class="import-tab-content">
            <form id="import-kml-form">
                <div class="form-group">
                    <label>Fichier KML ou KMZ</label>
                    <input type="file" name="file" accept=".kml,.kmz" class="form-control">
                </div>
                <div class="form-group">
                    <label>Dossier destination</label>
                    <select name="folder_id" class="form-control">
                        <option value="">-- Aucun --</option>
                        ${state.folders.map(f => `<option value="${f.id}">${escapeHtml(f.name)}</option>`).join('')}
                    </select>
                </div>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-upload"></i> Importer</button>
                </div>
            </form>
        </div>

        <div id="import-tab-gmaps" class="import-tab-content">
            <form id="import-gmaps-form">
                <div class="form-group">
                    <label>Lien Google Maps</label>
                    <input type="url" name="url" class="form-control" placeholder="https://maps.google.com/...">
                </div>
                <div class="form-group">
                    <label>Nom du point</label>
                    <input type="text" name="name" class="form-control" placeholder="Ex: Restaurant Paris">
                </div>
                <div class="form-group">
                    <label>Dossier destination</label>
                    <select name="folder_id" class="form-control">
                        <option value="">-- Aucun --</option>
                        ${state.folders.map(f => `<option value="${f.id}">${escapeHtml(f.name)}</option>`).join('')}
                    </select>
                </div>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-upload"></i> Importer</button>
                </div>
            </form>
        </div>
    `);

    // Form handlers
    document.getElementById('import-coords-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        try {
            const result = await api.importCoordinates(
                formData.get('text'),
                formData.get('folder_id') || null
            );
            closeModal();
            await loadData();
            showNotification(result.message, 'success');
        } catch (error) {
            showNotification(error.message, 'error');
        }
    });

    document.getElementById('import-kml-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const file = formData.get('file');
        if (!file || !file.name) {
            showNotification('Veuillez sélectionner un fichier', 'error');
            return;
        }
        try {
            const result = await api.importKml(file, formData.get('folder_id') || null);
            closeModal();
            await loadData();
            showNotification(result.message, 'success');
        } catch (error) {
            showNotification(error.message, 'error');
        }
    });

    document.getElementById('import-gmaps-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        try {
            await api.importGmaps(
                formData.get('url'),
                formData.get('name') || 'Point Google Maps',
                formData.get('folder_id') || null
            );
            closeModal();
            await loadData();
            showNotification('Point importé depuis Google Maps', 'success');
        } catch (error) {
            showNotification(error.message, 'error');
        }
    });

    // CSV/Excel file handler
    let csvData = null;
    let csvHeaders = [];

    document.getElementById('csv-file-input').addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Audit Fix B3: Reset du cache CSV à chaque nouveau fichier
        csvData = null;
        csvHeaders = [];

        try {
            const extension = file.name.split('.').pop().toLowerCase();

            if (extension === 'csv') {
                // Parse CSV
                const text = await file.text();
                const parsed = parseCSV(text);
                csvHeaders = parsed.headers;
                csvData = parsed.rows;
            } else if (['xlsx', 'xls'].includes(extension)) {
                // Parse Excel using SheetJS (if available)
                // Lazy-load SheetJS si nécessaire
                await ensureXLSX();
                const arrayBuffer = await file.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

                if (jsonData.length > 0) {
                    csvHeaders = jsonData[0].map(h => String(h || ''));
                    csvData = jsonData.slice(1).map(row => {
                        const obj = {};
                        csvHeaders.forEach((h, i) => obj[h] = row[i] || '');
                        return obj;
                    });
                }
            }

            if (csvHeaders.length > 0) {
                // Show preview
                showCsvPreview(csvHeaders, csvData.slice(0, 3));
                // Populate column selectors
                populateCsvColumnSelectors(csvHeaders);
                // Enable import button
                document.getElementById('csv-import-btn').disabled = false;
            }
        } catch (error) {
            console.error('Error parsing file:', error);
            showNotification('Erreur lors de la lecture du fichier', 'error');
        }
    });

    document.getElementById('import-csv-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!csvData || csvData.length === 0) {
            showNotification('Aucune donnée à importer', 'error');
            return;
        }

        const formData = new FormData(e.target);
        const colName = formData.get('col_name');
        const colLat = formData.get('col_lat');
        const colLng = formData.get('col_lng');
        const colDesc = formData.get('col_desc');
        const folderId = formData.get('folder_id') || null;

        if (!colLat || !colLng) {
            showNotification('Veuillez sélectionner les colonnes latitude et longitude', 'error');
            return;
        }

        try {
            const points = csvData
                .map(row => {
                    const lat = parseFloat(String(row[colLat]).replace(',', '.'));
                    const lng = parseFloat(String(row[colLng]).replace(',', '.'));

                    if (isNaN(lat) || isNaN(lng)) return null;

                    return {
                        name: colName ? (row[colName] || 'Point') : 'Point',
                        lat,
                        lng,
                        description: colDesc && row[colDesc] ? row[colDesc] : ''
                    };
                })
                .filter(p => p !== null);

            if (points.length === 0) {
                showNotification('Aucun point valide trouvé', 'error');
                return;
            }

            // Import via API
            const result = await api.importBulk(points, folderId);
            closeModal();
            await loadData();
            showNotification(`${points.length} points importés avec succès`, 'success');
        } catch (error) {
            showNotification(error.message, 'error');
        }
    });
}

// Helper function to parse CSV
function parseCSV(text) {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    // Detect delimiter
    const firstLine = lines[0];
    let delimiter = ',';
    if (firstLine.includes(';')) delimiter = ';';
    else if (firstLine.includes('\t')) delimiter = '\t';

    const headers = parseCSVLine(firstLine, delimiter);
    const rows = lines.slice(1).map(line => {
        const values = parseCSVLine(line, delimiter);
        const obj = {};
        headers.forEach((h, i) => obj[h] = values[i] || '');
        return obj;
    });

    return { headers, rows };
}

function parseCSVLine(line, delimiter) {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === delimiter && !inQuotes) {
            result.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }
    result.push(current.trim());

    return result;
}

function showCsvPreview(headers, rows) {
    const previewGroup = document.querySelector('.csv-preview-group');
    const preview = document.getElementById('csv-preview');

    if (!preview) return;

    let html = '<table class="csv-table"><thead><tr>';
    html += headers.map(h => `<th>${escapeHtml(h)}</th>`).join('');
    html += '</tr></thead><tbody>';

    rows.forEach(row => {
        html += '<tr>';
        html += headers.map(h => `<td>${escapeHtml(String(row[h] || ''))}</td>`).join('');
        html += '</tr>';
    });

    html += '</tbody></table>';
    html += `<small class="text-gray-500">${rows.length} premières lignes sur ${rows.length} affichées</small>`;

    preview.innerHTML = html;
    previewGroup.style.display = 'block';
    document.querySelector('.csv-mapping-group').style.display = 'block';
}

function populateCsvColumnSelectors(headers) {
    const selectors = ['csv-col-name', 'csv-col-lat', 'csv-col-lng', 'csv-col-desc'];

    selectors.forEach(id => {
        const select = document.getElementById(id);
        if (!select) return;

        const isOptional = id === 'csv-col-desc';
        select.innerHTML = isOptional ? '<option value="">-- Aucune --</option>' : '';

        headers.forEach(h => {
            const option = document.createElement('option');
            option.value = h;
            option.textContent = h;

            // Auto-select based on common names
            const hLower = h.toLowerCase();
            if (id === 'csv-col-name' && (hLower.includes('nom') || hLower.includes('name') || hLower === 'titre' || hLower === 'title')) {
                option.selected = true;
            } else if (id === 'csv-col-lat' && (hLower.includes('lat') || hLower === 'y')) {
                option.selected = true;
            } else if (id === 'csv-col-lng' && (hLower.includes('lng') || hLower.includes('lon') || hLower === 'x')) {
                option.selected = true;
            } else if (id === 'csv-col-desc' && (hLower.includes('desc') || hLower.includes('adresse') || hLower.includes('address'))) {
                option.selected = true;
            }

            select.appendChild(option);
        });
    });
}

function switchImportTab(tab) {
    document.querySelectorAll('.import-tab').forEach(t => t.classList.remove('active'));
    document.querySelectorAll('.import-tab-content').forEach(c => c.classList.remove('active'));

    // Audit Fix B2: Utiliser data-tab au lieu de onclick* (sélecteur robuste)
    const tabButton = document.querySelector(`.import-tab[data-tab="${tab}"]`);
    if (tabButton) tabButton.classList.add('active');
    document.getElementById(`import-tab-${tab}`).classList.add('active');
}

async function showTemplatesModal() {
    // Charger les templates depuis l'API
    let templatesHtml = '<p class="loading-text">Chargement...</p>';

    const modal = createModal('Templates de dossiers', `
        <p class="template-intro">Créez rapidement une structure de dossiers adaptée à votre activité.</p>
        <div id="templates-list" class="templates-list">
            ${templatesHtml}
        </div>
    `);

    try {
        const result = await api.getTemplates();
        if (result.success && result.templates) {
            const container = document.getElementById('templates-list');
            if (container) {
                container.innerHTML = result.templates.map(template => `
                    <div class="template-card" onclick="GeoManager.applyTemplate('${template.id}')">
                        <div class="template-icon">
                            <i class="fas fa-${getTemplateIcon(template.id)}"></i>
                        </div>
                        <div class="template-info">
                            <h4>${escapeHtml(template.name)}</h4>
                            <p>${escapeHtml(template.description)}</p>
                            <span class="template-badge">${template.folder_count} dossiers</span>
                        </div>
                        <div class="template-action">
                            <i class="fas fa-chevron-right"></i>
                        </div>
                    </div>
                `).join('');
            }
        }
    } catch (error) {
        const container = document.getElementById('templates-list');
        if (container) {
            container.innerHTML = `<p class="error-text">Erreur de chargement des templates</p>`;
        }
    }
}

function getTemplateIcon(templateId) {
    const icons = {
        'franchise': 'store',
        'commercial': 'briefcase',
        'logistique': 'truck',
        'evenementiel': 'calendar-alt',
        'immobilier': 'home',
    };
    return icons[templateId] || 'folder';
}

async function applyTemplate(templateId) {
    if (!confirm('Cela va créer plusieurs dossiers. Continuer ?')) return;

    try {
        const result = await api.applyTemplate(templateId);
        closeModal();
        await loadData();
        showNotification(result.message, 'success');
    } catch (error) {
        showNotification(error.message, 'error');
    }
}

/**
 * Génère le HTML du sélecteur d'icônes visuel
 * @param {string} selectedIcon - Icône actuellement sélectionnée
 * @param {string} selectedColor - Couleur actuellement sélectionnée
 * @param {string} iconType - Type d'icône ('preset' ou 'custom')
 * @returns {string} HTML du sélecteur
 */
function buildIconPicker(selectedIcon = 'default', selectedColor = null, iconType = 'preset') {
    const iconNames = {
        'default': 'Défaut',
        'store': 'Magasin',
        'restaurant': 'Restaurant',
        'hotel': 'Hôtel',
        'warehouse': 'Entrepôt',
        'office': 'Bureau',
        'factory': 'Usine',
        'hospital': 'Hôpital',
        'school': 'École',
        'gas': 'Station',
        'parking': 'Parking',
        'home': 'Maison',
        'star': 'Favori',
        'flag': 'Drapeau',
    };

    const isCustomSelected = iconType === 'custom';

    return `
        <div class="icon-picker-container">
            <div class="icon-picker-tabs">
                <button type="button" class="icon-picker-tab ${!isCustomSelected ? 'active' : ''}" onclick="GeoManager.switchIconTab('preset')">
                    <i class="fas fa-icons"></i> Prédéfinies
                </button>
                <button type="button" class="icon-picker-tab ${isCustomSelected ? 'active' : ''}" onclick="GeoManager.switchIconTab('custom')">
                    <i class="fas fa-image"></i> Personnalisées
                </button>
            </div>

            <div id="icon-picker-preset" class="icon-picker-content ${!isCustomSelected ? 'active' : ''}">
                <div class="icon-picker-grid">
                    ${Object.entries(config.icons.preset).map(([key, data]) => `
                        <button type="button"
                                class="icon-picker-item ${!isCustomSelected && key === selectedIcon ? 'selected' : ''}"
                                data-icon="${key}"
                                data-type="preset"
                                title="${iconNames[key] || key}"
                                onclick="GeoManager.selectIcon('${key}', 'preset')">
                            <div class="icon-picker-preview" style="background-color: ${selectedColor || data.color}">
                                <i class="fas ${data.icon}"></i>
                            </div>
                            <span class="icon-picker-label">${iconNames[key] || key}</span>
                        </button>
                    `).join('')}
                </div>
            </div>

            <div id="icon-picker-custom" class="icon-picker-content ${isCustomSelected ? 'active' : ''}">
                <div class="icon-picker-upload">
                    <label class="icon-upload-btn">
                        <i class="fas fa-cloud-upload-alt"></i>
                        <span>Ajouter une icône</span>
                        <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onchange="GeoManager.uploadCustomIcon(this.files[0])" style="display:none">
                    </label>
                    <small>PNG, JPG, SVG (max 500KB)</small>
                </div>
                <div id="custom-icons-grid" class="icon-picker-grid custom-icons">
                    ${state.customIcons.length === 0 ? `
                        <div class="icon-picker-empty">
                            <i class="fas fa-image"></i>
                            <p>Aucune icône personnalisée</p>
                        </div>
                    ` : state.customIcons.map(icon => `
                        <button type="button"
                                class="icon-picker-item ${isCustomSelected && icon.path === selectedIcon ? 'selected' : ''}"
                                data-icon="${icon.path}"
                                data-type="custom"
                                title="${icon.filename}"
                                onclick="GeoManager.selectIcon('${icon.path}', 'custom')">
                            <div class="icon-picker-preview custom-icon-preview">
                                <img src="${icon.path}" alt="${icon.filename}">
                            </div>
                            <span class="icon-picker-label">${icon.filename.substring(0, 8)}...</span>
                        </button>
                    `).join('')}
                </div>
            </div>
        </div>
        <input type="hidden" name="icon_type" value="${iconType}">
        <input type="hidden" name="icon_value" value="${selectedIcon}">
    `;
}

/**
 * Change l'onglet du sélecteur d'icônes
 * @param {string} tab - 'preset' ou 'custom'
 */
function switchIconTab(tab) {
    document.querySelectorAll('.icon-picker-tab').forEach(t => t.classList.remove('active'));
    document.querySelectorAll('.icon-picker-content').forEach(c => c.classList.remove('active'));

    document.querySelector(`.icon-picker-tab:${tab === 'preset' ? 'first' : 'last'}-child`).classList.add('active');
    document.getElementById(`icon-picker-${tab}`).classList.add('active');
}

/**
 * Met à jour la sélection d'icône dans le picker
 * @param {string} iconKey - Clé de l'icône sélectionnée
 * @param {string} type - Type ('preset' ou 'custom')
 */
function selectIcon(iconKey, type = 'preset') {
    // Mettre à jour les inputs hidden
    const typeInput = document.querySelector('input[name="icon_type"]');
    const valueInput = document.querySelector('input[name="icon_value"]');
    if (typeInput) typeInput.value = type;
    if (valueInput) valueInput.value = iconKey;

    // Mettre à jour la classe selected
    document.querySelectorAll('.icon-picker-item').forEach(item => {
        item.classList.remove('selected');
        if (item.dataset.icon === iconKey && item.dataset.type === type) {
            item.classList.add('selected');
        }
    });

    // Mettre à jour la couleur des previews preset avec la couleur sélectionnée
    if (type === 'preset') {
        const colorInput = document.querySelector('input[name="color"]');
        if (colorInput) {
            updateIconPickerColors(colorInput.value);
        }
    }
}

/**
 * Upload une icône custom
 * @param {File} file - Fichier image
 */
async function uploadCustomIcon(file) {
    if (!file) return;

    try {
        const result = await api.uploadIcon(file);
        if (result.success) {
            // Ajouter à la liste locale
            state.customIcons.push({
                filename: result.filename,
                path: result.path,
                size: result.size,
            });

            // Rafraîchir l'affichage
            refreshCustomIconsGrid();

            // Sélectionner automatiquement la nouvelle icône
            selectIcon(result.path, 'custom');

            showNotification('Icône ajoutée', 'success');
        }
    } catch (error) {
        showNotification(error.message, 'error');
    }
}

/**
 * Rafraîchit la grille des icônes custom
 */
function refreshCustomIconsGrid() {
    const grid = document.getElementById('custom-icons-grid');
    if (!grid) return;

    if (state.customIcons.length === 0) {
        grid.innerHTML = `
            <div class="icon-picker-empty">
                <i class="fas fa-image"></i>
                <p>Aucune icône personnalisée</p>
            </div>
        `;
    } else {
        grid.innerHTML = state.customIcons.map(icon => `
            <button type="button"
                    class="icon-picker-item"
                    data-icon="${icon.path}"
                    data-type="custom"
                    title="${icon.filename}"
                    onclick="GeoManager.selectIcon('${icon.path}', 'custom')">
                <div class="icon-picker-preview custom-icon-preview">
                    <img src="${icon.path}" alt="${icon.filename}">
                </div>
                <span class="icon-picker-label">${icon.filename.substring(0, 8)}...</span>
            </button>
        `).join('');
    }
}

/**
 * Met à jour les couleurs des icônes preset dans le picker
 * @param {string} color - Nouvelle couleur
 */
function updateIconPickerColors(color) {
    document.querySelectorAll('#icon-picker-preset .icon-picker-preview').forEach(preview => {
        preview.style.backgroundColor = color;
    });
}

/**
 * Charge les icônes custom de l'utilisateur
 */
async function loadCustomIcons() {
    try {
        const result = await api.getCustomIcons();
        if (result.success) {
            state.customIcons = result.icons || [];
        }
    } catch (error) {
        console.warn('Erreur chargement icônes custom:', error);
        state.customIcons = [];
    }
}

// ============================================================================
// Dashboard, Alertes, Recherche, Prédiction IA
// ============================================================================

/**
 * Affiche le dashboard temps réel
 */
async function showDashboardModal() {
    const modal = createModal('Dashboard Géo Manager', `
        <div id="geo-dashboard-content" class="geo-dashboard">
            <div class="loading-spinner">
                <i class="fas fa-spinner fa-spin"></i> Chargement...
            </div>
        </div>
    `, 'geo-dashboard-modal');

    try {
        const data = await api.getDashboard();
        if (data.success) {
            renderDashboard(data.dashboard);
        } else {
            document.getElementById('geo-dashboard-content').innerHTML = `
                <div class="alert alert-error">Erreur: ${data.error || 'Impossible de charger le dashboard'}</div>
            `;
        }
    } catch (error) {
        document.getElementById('geo-dashboard-content').innerHTML = `
            <div class="alert alert-error">Erreur: ${error.message}</div>
        `;
    }
}

function renderDashboard(dashboard) {
    const container = document.getElementById('geo-dashboard-content');
    const kpis = dashboard.kpis || {};
    const coverage = dashboard.coverage || {};
    const topZones = dashboard.top_zones || [];
    const alerts = dashboard.alerts || [];

    container.innerHTML = `
        <div class="dashboard-kpis">
            <div class="kpi-card">
                <div class="kpi-value">${kpis.total_points || 0}</div>
                <div class="kpi-label">Points</div>
            </div>
            <div class="kpi-card">
                <div class="kpi-value">${kpis.total_folders || 0}</div>
                <div class="kpi-label">Dossiers</div>
            </div>
            <div class="kpi-card">
                <div class="kpi-value">${formatNumber(kpis.total_population || 0)}</div>
                <div class="kpi-label">Population couverte</div>
            </div>
            <div class="kpi-card">
                <div class="kpi-value">${kpis.points_with_zones || 0}</div>
                <div class="kpi-label">Zones actives</div>
            </div>
        </div>

        <div class="dashboard-section">
            <h4><i class="fas fa-chart-pie"></i> Couverture</h4>
            <div class="coverage-stats">
                <p><strong>Population totale:</strong> ${formatNumber(coverage.total_population || 0)}</p>
                <p><strong>Ménages:</strong> ${formatNumber(coverage.total_menages || 0)}</p>
                <p><strong>Revenu médian moyen:</strong> ${formatNumber(coverage.avg_revenu_median || 0)} €</p>
            </div>
        </div>

        <div class="dashboard-section">
            <h4><i class="fas fa-trophy"></i> Top 5 Zones par Population</h4>
            ${topZones.length > 0 ? `
                <table class="dashboard-table">
                    <thead>
                        <tr>
                            <th>Point</th>
                            <th>Population</th>
                            <th>Ménages</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${topZones.map(z => `
                            <tr>
                                <td>${escapeHtml(z.point_name)}</td>
                                <td>${formatNumber(z.population)}</td>
                                <td>${formatNumber(z.menages)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            ` : '<p class="text-muted">Aucune zone avec données démographiques</p>'}
        </div>

        ${alerts.length > 0 ? `
            <div class="dashboard-section">
                <h4><i class="fas fa-exclamation-triangle"></i> Alertes (${alerts.length})</h4>
                <ul class="alert-list">
                    ${alerts.slice(0, 5).map(a => `
                        <li class="alert-item alert-${a.severity}">
                            <i class="fas ${getAlertIcon(a.type)}"></i>
                            ${escapeHtml(a.message)}
                        </li>
                    `).join('')}
                </ul>
                ${alerts.length > 5 ? `<p class="text-muted">... et ${alerts.length - 5} autres alertes</p>` : ''}
            </div>
        ` : ''}

        <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Fermer</button>
            <button type="button" class="btn btn-primary" onclick="GeoManager.showDiagnosticsModal()">
                <i class="fas fa-stethoscope"></i> Voir les diagnostics
            </button>
        </div>
    `;
}

function getAlertIcon(type) {
    const icons = {
        'duplicate': 'fa-clone',
        'incomplete': 'fa-exclamation-circle',
        'empty_folder': 'fa-folder-open',
        'coverage_gap': 'fa-map-marked-alt',
    };
    return icons[type] || 'fa-info-circle';
}

/**
 * Affiche les diagnostics ponctuels de qualité des données.
 */
async function showDiagnosticsModal() {
    const modal = createModal('Diagnostics des données', `
        <div id="geo-alerts-content">
            <div class="loading-spinner">
                <i class="fas fa-spinner fa-spin"></i> Analyse en cours...
            </div>
        </div>
    `, 'geo-alerts-modal');

    try {
        const data = await api.getDiagnostics({
            duplicateThreshold: 100,
            coverageThreshold: 10000,
        });

        if (data.success) {
            renderDiagnostics(data.alerts);
        } else {
            document.getElementById('geo-alerts-content').innerHTML = `
                <div class="alert alert-error">Erreur: ${data.error || 'Impossible de charger les diagnostics'}</div>
            `;
        }
    } catch (error) {
        document.getElementById('geo-alerts-content').innerHTML = `
            <div class="alert alert-error">Erreur: ${error.message}</div>
        `;
    }
}

function renderDiagnostics(items) {
    const container = document.getElementById('geo-alerts-content');
    const diagnostics = Array.isArray(items) ? items : [];
    const countByType = (type) => diagnostics.filter((item) => item?.type === type).length;

    container.innerHTML = `
        <div class="alerts-summary">
            <span class="badge badge-danger">${countByType('duplicate')} doublons</span>
            <span class="badge badge-warning">${countByType('incomplete')} incomplets</span>
            <span class="badge badge-info">${countByType('empty_folder')} dossiers vides</span>
            <span class="badge badge-secondary">${countByType('coverage_gap')} écarts de couverture</span>
        </div>

        ${diagnostics.length ? `
            <div class="alert-section">
                <h4><i class="fas fa-stethoscope" aria-hidden="true"></i> Résultats</h4>
                <ul class="alert-list">
                    ${diagnostics.map((item) => `
                        <li class="alert-item">
                            <strong>${escapeHtml(item.title || 'Diagnostic')}</strong>
                            <span>${escapeHtml(item.message || '')}</span>
                        </li>
                    `).join('')}
                </ul>
            </div>
        ` : `
            <div class="alert alert-success">
                <i class="fas fa-check-circle" aria-hidden="true"></i>
                Aucun problème détecté avec les contrôles sélectionnés.
            </div>
        `}

        <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Fermer</button>
        </div>
    `;
}

/**
 * Affiche le modal de recherche multi-critères
 */
function showSearchModal() {
    const modal = createModal('Recherche Multi-Critères', `
        <form id="geo-search-form">
            <div class="form-group">
                <label>Recherche texte</label>
                <input type="text" name="text" class="form-control" placeholder="Nom, description...">
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label>Population min</label>
                    <input type="number" name="population_min" class="form-control" placeholder="Ex: 10000">
                </div>
                <div class="form-group">
                    <label>Population max</label>
                    <input type="number" name="population_max" class="form-control" placeholder="Ex: 100000">
                </div>
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label>Revenu médian min</label>
                    <input type="number" name="revenu_min" class="form-control" placeholder="Ex: 20000">
                </div>
                <div class="form-group">
                    <label>Revenu médian max</label>
                    <input type="number" name="revenu_max" class="form-control" placeholder="Ex: 50000">
                </div>
            </div>

            <div class="form-group">
                <label>Dossier</label>
                <select name="folder_id" class="form-control">
                    <option value="">-- Tous les dossiers --</option>
                    ${state.folders.map(f => `<option value="${f.id}">${escapeHtml(f.name)}</option>`).join('')}
                </select>
            </div>

            <div class="form-group">
                <label>Points avec zone uniquement</label>
                <input type="checkbox" name="has_zone" value="true">
            </div>

            <div class="form-actions">
                <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                <button type="submit" class="btn btn-primary">
                    <i class="fas fa-search"></i> Rechercher
                </button>
            </div>
        </form>

        <div id="geo-search-results" style="display: none; margin-top: 1rem;"></div>
    `);

    document.getElementById('geo-search-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);

        const criteria = {};
        const text = formData.get('text');
        if (text) criteria.text = text;

        const popMin = formData.get('population_min');
        if (popMin) criteria.population_min = parseInt(popMin);

        const popMax = formData.get('population_max');
        if (popMax) criteria.population_max = parseInt(popMax);

        const revMin = formData.get('revenu_min');
        if (revMin) criteria.revenu_min = parseInt(revMin);

        const revMax = formData.get('revenu_max');
        if (revMax) criteria.revenu_max = parseInt(revMax);

        const folderId = formData.get('folder_id');
        if (folderId) criteria.folder_id = folderId;

        if (formData.get('has_zone')) criteria.has_zone = true;

        await executeSearch(criteria);
    });
}

async function executeSearch(criteria) {
    const resultsDiv = document.getElementById('geo-search-results');
    resultsDiv.style.display = 'block';
    resultsDiv.innerHTML = '<div class="loading-spinner"><i class="fas fa-spinner fa-spin"></i> Recherche...</div>';

    try {
        const data = await api.search(criteria);

        if (data.success) {
            const points = data.results || [];
            resultsDiv.innerHTML = `
                <h4>${points.length} résultat(s)</h4>
                ${points.length > 0 ? `
                    <ul class="search-results-list">
                        ${points.map(p => `
                            <li class="search-result-item" onclick="GeoManager.centerOnPoint('${p.id}'); GeoManager.closeModal();">
                                <i class="fas fa-map-marker-alt" style="color: ${p.color || config.defaultColor}"></i>
                                <div>
                                    <strong>${escapeHtml(p.name)}</strong>
                                    ${p.demographics ? `
                                        <small>Pop: ${formatNumber(p.demographics.population)} | Rev: ${formatNumber(p.demographics.revenu_median)}€</small>
                                    ` : ''}
                                </div>
                            </li>
                        `).join('')}
                    </ul>
                ` : '<p class="text-muted">Aucun résultat</p>'}
            `;
        } else {
            resultsDiv.innerHTML = `<div class="alert alert-error">${data.error}</div>`;
        }
    } catch (error) {
        resultsDiv.innerHTML = `<div class="alert alert-error">${error.message}</div>`;
    }
}

/**
 * Affiche le modal de prédiction IA
 */
function showPredictionModal() {
    const center = state.map ? state.map.getCenter() : { lat: 46.603354, lng: 1.888334 };

    const modal = createModal('Prédiction IA - Sites Optimaux', `
        <form id="geo-prediction-form">
            <p class="form-help">
                L'algorithme analyse la population, les revenus et les points existants
                pour suggérer les meilleurs emplacements pour de nouveaux sites.
            </p>

            <div class="form-row">
                <div class="form-group">
                    <label>Latitude centre</label>
                    <input type="number" name="center_lat" value="${center.lat.toFixed(4)}" step="0.0001" class="form-control">
                </div>
                <div class="form-group">
                    <label>Longitude centre</label>
                    <input type="number" name="center_lng" value="${center.lng.toFixed(4)}" step="0.0001" class="form-control">
                </div>
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label>Rayon de recherche (km)</label>
                    <input type="number" name="radius_km" value="50" min="10" max="200" class="form-control">
                </div>
                <div class="form-group">
                    <label>Nombre de suggestions</label>
                    <input type="number" name="num_suggestions" value="5" min="1" max="20" class="form-control">
                </div>
            </div>

            <div class="form-group">
                <label>Secteur d'activité</label>
                <select name="sector" class="form-control">
                    <option value="commerce">Commerce de détail</option>
                    <option value="restauration">Restauration</option>
                    <option value="services">Services</option>
                    <option value="sante">Santé</option>
                </select>
            </div>

            <div class="form-actions">
                <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                <button type="submit" class="btn btn-primary">
                    <i class="fas fa-magic"></i> Analyser
                </button>
            </div>
        </form>

        <div id="geo-prediction-results" style="display: none; margin-top: 1rem;"></div>
    `);

    document.getElementById('geo-prediction-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);

        await executePrediction({
            centerLat: parseFloat(formData.get('center_lat')),
            centerLng: parseFloat(formData.get('center_lng')),
            radiusKm: parseInt(formData.get('radius_km')),
            numSuggestions: parseInt(formData.get('num_suggestions')),
            sector: formData.get('sector'),
        });
    });
}

async function executePrediction(options) {
    const resultsDiv = document.getElementById('geo-prediction-results');
    resultsDiv.style.display = 'block';
    resultsDiv.innerHTML = '<div class="loading-spinner"><i class="fas fa-spinner fa-spin"></i> Analyse IA en cours...</div>';

    try {
        const data = await api.suggestLocations(options);

        if (data.success) {
            const suggestions = data.suggestions || [];
            resultsDiv.innerHTML = `
                <h4><i class="fas fa-lightbulb text-warning"></i> ${suggestions.length} Emplacements Suggérés</h4>
                ${suggestions.length > 0 ? `
                    <div class="prediction-results">
                        ${suggestions.map((s, i) => `
                            <div class="prediction-item">
                                <div class="prediction-rank">#${i + 1}</div>
                                <div class="prediction-info">
                                    <strong>${escapeHtml(s.commune_name)}</strong>
                                    <small>Score: ${(s.score * 100).toFixed(0)}%</small>
                                    <div class="prediction-details">
                                        <span><i class="fas fa-users"></i> ${formatNumber(s.population)}</span>
                                        <span><i class="fas fa-euro-sign"></i> ${formatNumber(s.revenu_median)}</span>
                                        <span><i class="fas fa-store"></i> ${s.competitors_nearby} concurrents</span>
                                    </div>
                                </div>
                                <button class="btn btn-sm btn-primary" onclick="GeoManager.addPredictedPoint(${s.lat}, ${s.lng}, '${escapeHtml(s.commune_name)}')">
                                    <i class="fas fa-plus"></i> Ajouter
                                </button>
                            </div>
                        `).join('')}
                    </div>
                ` : '<p class="text-muted">Aucune suggestion disponible</p>'}
            `;
        } else {
            resultsDiv.innerHTML = `<div class="alert alert-error">${data.error}</div>`;
        }
    } catch (error) {
        resultsDiv.innerHTML = `<div class="alert alert-error">${error.message}</div>`;
    }
}

/**
 * Ajoute un point suggéré par la prédiction IA
 */
async function addPredictedPoint(lat, lng, name) {
    closeModal();
    showQuickAddPointModal(lat, lng);
    // Pré-remplir le nom
    setTimeout(() => {
        const nameInput = document.querySelector('#quick-add-point-form input[name="name"]');
        if (nameInput) {
            nameInput.value = `Nouveau site - ${name}`;
        }
    }, 100);
}

/**
 * Affiche le modal de duplication de dossier
 */
function showDuplicateFolderModal(folderId) {
    const folder = state.folders.find(f => f.id === folderId);
    if (!folder) return;

    const pointCount = state.points.filter(p => p.folder_id === folderId).length;

    const modal = createModal('Dupliquer le dossier', `
        <form id="geo-duplicate-form">
            <p>Duplication de "<strong>${escapeHtml(folder.name)}</strong>" (${pointCount} points)</p>

            <div class="form-group">
                <label>Nouveau nom</label>
                <input type="text" name="new_name" value="${escapeHtml(folder.name)} (copie)" required class="form-control">
            </div>

            <h4 class="mt-4">Décalage géographique (optionnel)</h4>
            <p class="form-help">Pour déplacer tous les points vers une nouvelle région</p>

            <div class="form-row">
                <div class="form-group">
                    <label>Décalage latitude</label>
                    <input type="number" name="offset_lat" value="0" step="0.01" class="form-control">
                </div>
                <div class="form-group">
                    <label>Décalage longitude</label>
                    <input type="number" name="offset_lng" value="0" step="0.01" class="form-control">
                </div>
            </div>

            <div class="form-group">
                <label>
                    <input type="checkbox" name="include_zones" checked>
                    Copier les zones de chalandise
                </label>
            </div>

            <div class="form-group">
                <label>
                    <input type="checkbox" name="include_metadata" checked>
                    Copier les métadonnées
                </label>
            </div>

            <div class="form-actions">
                <button type="button" class="btn btn-secondary" onclick="GeoManager.closeModal()">Annuler</button>
                <button type="submit" class="btn btn-primary">
                    <i class="fas fa-copy"></i> Dupliquer
                </button>
            </div>
        </form>
    `);

    document.getElementById('geo-duplicate-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);

        try {
            const result = await api.duplicateFolder(folderId, {
                newName: formData.get('new_name'),
                offsetLat: parseFloat(formData.get('offset_lat')) || 0,
                offsetLng: parseFloat(formData.get('offset_lng')) || 0,
                includeZones: formData.get('include_zones') === 'on',
                includeMetadata: formData.get('include_metadata') === 'on',
            });

            if (result.success) {
                closeModal();
                await loadData();
                showNotification(`Dossier dupliqué: ${result.new_folder.name} (${result.points_copied} points)`, 'success');
            } else {
                showNotification(result.error || 'Erreur de duplication', 'error');
            }
        } catch (error) {
            showNotification(error.message, 'error');
        }
    });
}

/**
 * Export des données démographiques
 */
async function exportDemographics(format = 'json') {
    try {
        showNotification('Génération de l\'export en cours...', 'info');

        const result = await api.exportDemographics(state.selectedFolder, 10, format);

        if (result.success) {
            if (result.download_url) {
                // Télécharger le fichier
                window.location.href = result.download_url;
            } else {
                // Données JSON retournées directement
                showNotification('Export généré', 'success');
            }
        } else {
            showNotification(result.error || 'Erreur export', 'error');
        }
    } catch (error) {
        showNotification(error.message, 'error');
    }
}

// ============================================================================
// Actions CRUD
// ============================================================================

async function deleteFolder(folderId) {
    const folder = state.folders.find(f => f.id === folderId);
    if (!folder) return;

    const pointCount = state.points.filter(p => p.folder_id === folderId).length;
    const message = pointCount > 0
        ? `Supprimer "${folder.name}" et ses ${pointCount} points ?`
        : `Supprimer le dossier "${folder.name}" ?`;

    if (!confirm(message)) return;

    try {
        await api.deleteFolder(folderId);
        if (state.selectedFolder === folderId) {
            state.selectedFolder = null;
        }
        await loadData();
        showNotification('Dossier supprimé', 'success');
    } catch (error) {
        showNotification(error.message, 'error');
    }
}

async function deletePoint(pointId) {
    const point = state.points.find(p => p.id === pointId);
    if (!point) return;

    if (!confirm(`Supprimer le point "${point.name}" ?`)) return;

    try {
        await api.deletePoint(pointId);
        if (state.selectedPoint === pointId) {
            state.selectedPoint = null;
            document.getElementById('geo-point-details').innerHTML = '';
        }
        await loadData();
        showNotification('Point supprimé', 'success');
    } catch (error) {
        showNotification(error.message, 'error');
    }
}

// ============================================================================
// Données
// ============================================================================

async function loadData() {
    // Audit Fix A2: Prévenir les appels concurrents (race condition)
    if (state.loadDataInProgress) {
        // Si déjà en cours, retourner la promesse existante
        if (state.loadDataPromise) {
            return state.loadDataPromise;
        }
        // Sinon attendre que le précédent se termine
        return new Promise(resolve => {
            const checkInterval = setInterval(() => {
                if (!state.loadDataInProgress) {
                    clearInterval(checkInterval);
                    resolve(loadData());
                }
            }, 100);
        });
    }

    state.loadDataInProgress = true;
    state.loadDataPromise = (async () => {
        try {
            const [foldersRes, pointsRes] = await Promise.all([
                api.getFolders(),
                api.getPoints(),
            ]);

            state.folders = foldersRes.folders || [];
            state.points = pointsRes.points || [];

            renderFolderTree();
            renderPoints();

            // Mettre à jour les stats
            updateStats();

            // Audit Fix C4: Marquer l'état comme changé
            state.stateChanged = true;
        } catch (error) {
            console.error('Erreur chargement données:', error);
            showNotification('Erreur de chargement des données', 'error');
        } finally {
            state.loadDataInProgress = false;
            state.loadDataPromise = null;
        }
    })();

    return state.loadDataPromise;
}

function updateStats() {
    const statsEl = document.getElementById('geo-stats');
    if (statsEl) {
        statsEl.innerHTML = `
            <span><i class="fas fa-folder"></i> ${state.folders.length} dossiers</span>
            <span><i class="fas fa-map-marker-alt"></i> ${state.points.length} points</span>
        `;
    }
}

// ============================================================================
// Initialization
// ============================================================================

let initPromise = null;

async function init() {
    if (state.initialized) return;

    if (initPromise) {
        return initPromise;
    }

    initPromise = (async () => {
        try {
            loadFromLocalStorage();
            initMap(showQuickAddPointModal);

            await Promise.all([
                loadData(),
                loadCustomIcons(),
            ]);

            setInterval(saveToLocalStorage, 30000);
            state.initialized = true;
        } catch (error) {
            console.error('GeoManager: Erreur initialisation', error);
            initPromise = null;
            throw error;
        }
    })();

    return initPromise;
}

// ============================================================================
// Public API (window.GeoManager for template onclick compatibility)
// ============================================================================

const GeoManager = {
    init,
    loadData,

    // Folders
    selectFolder,
    toggleFolderExpand,
    toggleFolderVisibility,
    showCreateFolderModal,
    showEditFolderModal,
    deleteFolder,

    // Points
    selectPoint,
    centerOnPoint,
    showEditPointModal,
    deletePoint,

    // Import/Export
    showImportModal,
    switchImportTab,

    // Templates
    showTemplatesModal,
    applyTemplate,

    // Drag & Drop
    handleFolderDragStart,
    handleFolderDragOver,
    handleFolderDragLeave,
    handleFolderDrop,
    handlePointDragStart,
    handlePointDropOnFolder,

    // Modal
    closeModal,

    // Icon picker
    selectIcon,
    switchIconTab,
    uploadCustomIcon,
    updateIconColors: updateIconPickerColors,

    // Map controls
    zoomIn: () => state.map && state.map.zoomIn(),
    zoomOut: () => state.map && state.map.zoomOut(),
    fitAllPoints: () => {
        if (!state.map) {
            showNotification("La carte n'est pas prête", 'warning');
            return;
        }
        const validPoints = state.points.filter(p => p && typeof p.lat === 'number' && typeof p.lng === 'number');
        if (validPoints.length === 0) {
            showNotification('Aucun point à afficher', 'info');
            return;
        }
        try {
            const bounds = L.latLngBounds(validPoints.map(p => [p.lat, p.lng]));
            state.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
        } catch (error) {
            showNotification('Erreur lors du zoom', 'error');
        }
    },
    toggleClustering: () => {
        if (!state.markerClusterGroup || !state.map) return;
        state.clusteringEnabled = !state.clusteringEnabled;
        if (state.clusteringEnabled) {
            state.map.addLayer(state.markerClusterGroup);
            Object.values(state.markers).forEach(marker => {
                state.map.removeLayer(marker);
                state.markerClusterGroup.addLayer(marker);
            });
        } else {
            state.markerClusterGroup.clearLayers();
            state.map.removeLayer(state.markerClusterGroup);
            Object.values(state.markers).forEach(marker => {
                marker.addTo(state.map);
            });
        }
        const btn = document.getElementById('geo-cluster-toggle');
        if (btn) {
            btn.classList.toggle('active', state.clusteringEnabled);
            btn.title = state.clusteringEnabled ? 'Désactiver le clustering' : 'Activer le clustering';
        }
        return state.clusteringEnabled;
    },
    isClusteringEnabled: () => state.clusteringEnabled,
    toggleFullscreen: () => {
        const panel = document.getElementById('geoManagerPanel');
        if (panel) {
            panel.classList.toggle('geo-fullscreen');
            const btn = panel.querySelector('.geo-fullscreen-btn i');
            if (btn) {
                if (panel.classList.contains('geo-fullscreen')) {
                    btn.classList.remove('fa-expand');
                    btn.classList.add('fa-compress');
                } else {
                    btn.classList.remove('fa-compress');
                    btn.classList.add('fa-expand');
                }
            }
            if (state.map) {
                setTimeout(() => state.map.invalidateSize(), 100);
            }
        }
    },

    // Analyses
    loadPointDemographics,
    loadPointCompetitors,
    toggleHeatmap,
    showHeatmapModal,
    applyHeatmap,
    toggleConvexHull,
    showConvexHullModal,
    applyConvexHull,
    toggleLegend,
    showCoverageModal,
    calculateCoverage,
    toggleZoneType,
    generateZoneForPoint,

    // Export
    showExportModal,
    exportPNG,
    exportPDF,
    exportSVG,
    exportGeoJSON,
    exportKML,
    exportDemographics,

    // Sharing
    showShareModal,
    createShare,
    copyShareLink,

    // Routes
    showRouteModal,
    optimizeRoute,
    drawRoute,
    drawLastRoute,
    clearRoute,

    // Dashboard, diagnostics, Search, Prediction
    showDashboardModal,
    showDiagnosticsModal,
    showSearchModal,
    showPredictionModal,
    addPredictedPoint,
    showDuplicateFolderModal,

    // State access (debug)
    getState: () => state,
    getMap: () => state.map,

    // Cleanup
    destroy() {
        eventManager.removeAll();
        if (state.map) {
            if (state.mapClickHandler) state.map.off('click', state.mapClickHandler);
            if (state.heatmapLayer) state.map.removeLayer(state.heatmapLayer);
            if (state.convexHullLayer) state.map.removeLayer(state.convexHullLayer);
            if (state.markerClusterGroup) state.map.removeLayer(state.markerClusterGroup);
            if (state.routePolyline) state.map.removeLayer(state.routePolyline);
            state.routeMarkers.forEach(marker => state.map.removeLayer(marker));
            Object.values(state.markers).forEach(marker => { if (marker) state.map.removeLayer(marker); });
            Object.values(state.zones).forEach(zone => { if (zone && zone.layer) state.map.removeLayer(zone.layer); });
            if (state.legendControl) state.map.removeControl(state.legendControl);
            state.map.remove();
        }
        state.map = null;
        state.markers = {};
        state.zones = {};
        state.markerClusterGroup = null;
        state.heatmapLayer = null;
        state.convexHullLayer = null;
        state.legendControl = null;
        state.routePolyline = null;
        state.routeMarkers = [];
        state.initialized = false;
        state.folders = [];
        state.points = [];
    },
};

window.GeoManager = GeoManager;

// Auto-init when container exists
document.addEventListener('DOMContentLoaded', function() {
    if (document.getElementById('geo-manager-container')) {
        GeoManager.init();
    }

    const geoTab = document.querySelector('[data-target="geoManagerPanel"]');
    if (geoTab) {
        let initialized = false;
        geoTab.addEventListener('click', function() {
            if (!initialized) {
                setTimeout(() => {
                    GeoManager.init();
                    initialized = true;
                }, 100);
            } else {
                const map = GeoManager.getMap();
                if (map) {
                    setTimeout(() => map.invalidateSize(), 50);
                }
            }
        });
    }
});
